import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import StakeTable, { Column } from './GameEngine/StakeTable';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../services/apiClient';
import { useGeneralSettings, GeneralConfig, Banner } from '../hooks/useGeneralSettings';
import appConfig from '../config';
import { toast } from 'react-toastify';
import JoditEditor from 'jodit-react';
import styled, { keyframes, createGlobalStyle } from 'styled-components';
import * as LucideIcons from 'lucide-react';
import ImageCropModal from '../components/ui/ImageCropModal';
import { getTimezoneListWithOffsets } from '../utils/dateUtils';

const JoditThemeOverride = createGlobalStyle`
  /* Override Jodit editor main background and border to slate/dark colors globally */
  .jodit-container {
    background-color: #0f172a !important; /* slate-900 */
    border-color: #1e293b !important; /* slate-800 */
    color: #f8fafc !important;
  }
  .jodit-container .jodit-wysiwyg {
    background-color: #0b0f19 !important; /* editor body background */
    color: #f1f5f9 !important; /* white text */
  }
  .jodit-container .jodit-workplace {
    background-color: #0b0f19 !important;
  }
  .jodit-container .jodit-toolbar__box {
    background-color: #0f172a !important;
    border-bottom: 1px solid #1e293b !important;
  }
  .jodit-container .jodit-toolbar-button {
    color: #cbd5e1 !important;
  }
  .jodit-container .jodit-toolbar-button:hover {
    background-color: #1e293b !important;
  }
  .jodit-container .jodit-status-bar {
    background-color: #0f172a !important;
    border-top: 1px solid #1e293b !important;
    color: #94a3b8 !important;
  }
`;

const SOCIAL_ICONS_LIST = [
  "Send", "MessageCircle", "Facebook", "Twitter", "Instagram", "Youtube",
  "Headphones", "Mail", "Phone", "Globe", "MessageSquare", "MessagesSquare",
  "Twitch", "Slack", "Github", "Linkedin", "Reddit"
];

const pulse = keyframes`
  0% { opacity: 0.6; }
  50% { opacity: 1; }
  100% { opacity: 0.6; }
`;

interface CmsPage {
  id: number;
  key: string;
  title: string;
  content: string;
  meta_title: string;
  meta_description: string;
  is_active: boolean;
  updated_at?: string;
}

export default function CmsPageManager() {
  const queryClient = useQueryClient();
  const editorRef = useRef(null);
  const [searchParams] = useSearchParams();

  // ─── TABS 1: DYNAMIC PAGES STATE ───
  const [selectedKey, setSelectedKey] = useState<string>('privacy_policy');
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [keyInput, setKeyInput] = useState('');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [metaTitle, setMetaTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [isActive, setIsActive] = useState(true);

  // ─── TABS 2 & 3: GENERAL SETTINGS (LOGOS & BANNERS) HOOK ───
  const {
    configQuery,
    bannersQuery,
    updateConfigMutation,
    addBannerMutation,
    deleteBannerMutation,
    toggleBannerMutation,
    updateBannerMutation
  } = useGeneralSettings();

  const rawActiveCmsTab = (searchParams.get('tab') as 'pages' | 'banners' | 'logos' | 'footer' | 'social' | 'help' | 'game_cards' | 'affiliate') || 'pages';
  const activeDesign = configQuery?.data?.active_design || 'bc';
  const activeCmsTab = (rawActiveCmsTab === 'game_cards' && activeDesign === 'stake') ? 'pages' : rawActiveCmsTab;

  const [bannersPage, setBannersPage] = useState(1);
  const [bannersLimit, setBannersLimit] = useState(10);

  const paginatedBannersQuery = useQuery({
    queryKey: ['banners', 'paginated', activeDesign, bannersPage, bannersLimit],
    queryFn: async () => {
      const res = await apiClient.get('/admin/website-banners', {
        params: {
          theme: activeDesign,
          page: bannersPage,
          limit: bannersLimit
        },
        headers: { authorization: localStorage.getItem('token') }
      });
      return res.data;
    }
  });

  const activeBanners = paginatedBannersQuery.data?.data || [];
  const bannersPagination = paginatedBannersQuery.data?.pagination || { total: 0, totalPages: 0 };

  // Local state for logos/branding config
  const [brandConfig, setBrandConfig] = useState<GeneralConfig | null>(null);
  const [logoSmallFile, setLogoSmallFile] = useState<File | null>(null);
  const [logoBigFile, setLogoBigFile] = useState<File | null>(null);
  const [faviconFile, setFaviconFile] = useState<File | null>(null);
  const [appFile, setAppFile] = useState<File | null>(null);

  // Local state for banner uploads
  const [newBanner, setNewBanner] = useState({
    type: 'home',
    link_url: '',
    sort_order: 0,
    tag: '',
    heading: '',
    subheading: '',
    button_text: '',
    button_link: '',
    gradient: '',
    theme: 'bc',
    is_active: true
  });
  const [bannerFile, setBannerFile] = useState<File | null>(null);
  const [editingBanner, setEditingBanner] = useState<any | null>(null);
  const [editBannerFile, setEditBannerFile] = useState<File | null>(null);
  const [isAddingBanner, setIsAddingBanner] = useState(false);

  // Banner Cropping & Preview States
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);
  const [cropTarget, setCropTarget] = useState<'new' | 'edit'>('new');
  const [bannerLocalPreview, setBannerLocalPreview] = useState<string | null>(null);
  const [editBannerLocalPreview, setEditBannerLocalPreview] = useState<string | null>(null);
  const bannerFileInputRef = useRef<HTMLInputElement | null>(null);
  const editBannerFileInputRef = useRef<HTMLInputElement | null>(null);

  const isSimpleBannerType = (type: string) => ['promotions', 'missions', 'offers', 'sidebar'].includes(type);

  const getBannerCropInfo = (type: string, theme: string = 'stake') => {
    switch (type) {
      case 'promotions':
      case 'missions':
      case 'offers':
        return {
          aspect: 5,
          title: `Crop ${type.charAt(0).toUpperCase() + type.slice(1)} Banner (5:1 Sleek Banner)`,
          recommendedText: 'Full-width sleek page banner. Aspect ratio 5:1 (e.g. 1100×220px or 1250×250px).'
        };
      case 'home':
        return {
          aspect: 4 / 3,
          title: 'Crop Homepage Category Card (4:3)',
          recommendedText: 'Homepage Category Card. Aspect ratio 4:3 (e.g. 600×450px or 400×300px).'
        };
      case 'casino':
      case 'sports':
        if (theme === 'stake') {
          return {
            aspect: 1,
            title: `Crop ${type === 'casino' ? 'Casino' : 'Sports'} Artwork (1:1 Square)`,
            recommendedText: 'Stake Slider Card Artwork (Square 1:1, e.g. 500×500px character/game art).'
          };
        } else {
          return {
            aspect: 2 / 1,
            title: `Crop ${type === 'casino' ? 'Casino' : 'Sports'} Slider Banner (2:1)`,
            recommendedText: 'BC Header Slider Banner. Aspect ratio 2:1 (e.g. 1200×600px or 600×300px).'
          };
        }
      case 'sidebar':
        return {
          aspect: 1,
          title: 'Crop Sidebar Promo Card (1:1 Square)',
          recommendedText: 'Sidebar Promo Card. Aspect ratio 1:1 Square (e.g. 500×500px).'
        };
      default:
        return {
          aspect: 16 / 9,
          title: 'Crop Banner Image (16:9)',
          recommendedText: 'Standard widescreen banner format (16:9, e.g. 1920×1080px).'
        };
    }
  };

  const handleBannerFileSelect = (file: File, target: 'new' | 'edit') => {
    const localUrl = URL.createObjectURL(file);
    if (target === 'new') {
      setBannerFile(file);
      setBannerLocalPreview(localUrl);
    } else {
      setEditBannerFile(file);
      setEditBannerLocalPreview(localUrl);
    }

    const reader = new FileReader();
    reader.addEventListener('load', () => {
      setCropImageSrc(reader.result?.toString() || null);
      setCropTarget(target);
      setCropModalOpen(true);
    });
    reader.readAsDataURL(file);
  };

  // Sync brand config from react-query hook
  useEffect(() => {
    if (configQuery.data) {
      setBrandConfig(configQuery.data);
    }
  }, [configQuery.data]);

  // ─── TABS 4: FOOTER SETTINGS STATE ───
  const [footerDescription, setFooterDescription] = useState('');
  const [footerCopyright, setFooterCopyright] = useState('');
  const [footerSections, setFooterSections] = useState<any[]>([]);

  useEffect(() => {
    if (configQuery.data) {
      setFooterDescription(configQuery.data.footer_description || '');
      setFooterCopyright(configQuery.data.footer_copyright || '');
      setFooterSections(configQuery.data.footer_sections || []);
    }
  }, [configQuery.data]);

  const handleUpdateFooter = (e: React.FormEvent) => {
    e.preventDefault();
    if (!configQuery.data) return;
    updateConfigMutation.mutate(
      {
        config: {
          ...configQuery.data,
          footer_description: footerDescription,
          footer_copyright: footerCopyright,
          footer_sections: footerSections,
        },
        logoSmallFile: null,
        logoBigFile: null,
        faviconFile: null
      },
      {
        onSuccess: () => {
          toast.success('Footer settings updated successfully!');
        },
        onError: (err) => {
          console.error(err);
          toast.error('Failed to update footer settings');
        }
      }
    );
  };

  const handleAddSection = () => {
    setFooterSections([...footerSections, { title: 'New Column', links: [] }]);
  };

  const handleRemoveSection = (sectionIndex: number) => {
    const updated = footerSections.filter((_, idx) => idx !== sectionIndex);
    setFooterSections(updated);
  };

  const handleSectionTitleChange = (sectionIndex: number, newTitle: string) => {
    const updated = [...footerSections];
    updated[sectionIndex].title = newTitle;
    setFooterSections(updated);
  };

  const handleAddLink = (sectionIndex: number) => {
    const updated = [...footerSections];
    updated[sectionIndex].links.push({ label: 'New Link', to: '/', external: false });
    setFooterSections(updated);
  };

  const handleRemoveLink = (sectionIndex: number, linkIndex: number) => {
    const updated = [...footerSections];
    updated[sectionIndex].links = updated[sectionIndex].links.filter((_: any, idx: number) => idx !== linkIndex);
    setFooterSections(updated);
  };

  const handleLinkChange = (sectionIndex: number, linkIndex: number, field: string, value: any) => {
    const updated = [...footerSections];
    updated[sectionIndex].links[linkIndex] = {
      ...updated[sectionIndex].links[linkIndex],
      [field]: value
    };
    setFooterSections(updated);
  };

  // ─── TABS 5: SOCIAL LINKS STATE ───
  const [socialLinks, setSocialLinks] = useState<any[]>([]);
  const [editingSocialLink, setEditingSocialLink] = useState<any | null>(null);
  const [isAddingSocialLink, setIsAddingSocialLink] = useState(false);
  const [socialFile, setSocialFile] = useState<File | null>(null);

  // Sync social links
  useEffect(() => {
    if (configQuery.data) {
      setSocialLinks(configQuery.data.social_links || []);
    }
  }, [configQuery.data]);

  // ─── TABS 6: HELP CENTER STATE ───
  const [helpSearchQuery, setHelpSearchQuery] = useState('');
  const [helpIsEditing, setHelpIsEditing] = useState(false);
  const [helpIsCreatingNew, setHelpIsCreatingNew] = useState(false);
  const [helpSelectedId, setHelpSelectedId] = useState<number | null>(null);
  const [helpTitle, setHelpTitle] = useState('');
  const [helpSlug, setHelpSlug] = useState('');
  const [helpContent, setHelpContent] = useState('');
  const [helpIconUrl, setHelpIconUrl] = useState('');
  const [helpSortOrder, setHelpSortOrder] = useState<number>(0);
  const [helpIsActive, setHelpIsActive] = useState(true);

  // ─── TABS 7: GAME CARDS STATE ───
  const [gameCards, setGameCards] = useState<any[]>([]);

  useEffect(() => {
    if (configQuery.data) {
      const defaultCards = [
        { id: "card-1", name: "Casino", action_type: "launch_game", path: "", bg_image: "/assets/bcimages/bchome/casino-game-card.webp", icon: "/assets/bcimages/bchome/casino-home-gamecard.svg", desc: "Casino" },
        { id: "card-2", name: "SPORTS", action_type: "redirect", path: "/casino/sports", bg_image: "/assets/bcimages/bchome/sports-game-card.webp", icon: "/assets/bcimages/bchome/sports-home-gamecard.svg", desc: "Bet on Football, Cricket, NFL, eSports & over 80 sports!" },
        { id: "card-3", name: "SPRIBE", action_type: "redirect", path: "/providers/300", bg_image: "/assets/images/bccrash.png", icon: "/assets/bcimages/bchome/lottery-home-gamecard.svg", desc: "" },
        { id: "card-4", name: "LIVE", action_type: "redirect", path: "/casino", bg_image: "/assets/images/live-card.png", icon: "/assets/bcimages/bchome/racing-home-gamecard.svg", desc: "" },
        { id: "card-5", name: "SLOTS", action_type: "redirect", path: "/casino/slots", bg_image: "/assets/images/bcslots.png", icon: "/assets/bcimages/bchome/updown-home-gamecard.svg", desc: "" },
        { id: "card-6", name: "EZUGI", action_type: "redirect", path: "/casino/live-casino", bg_image: "/assets/images/ezugi-live.png", icon: "/assets/bcimages/bchome/bingo-home-gamecard.svg", desc: "" }
      ];
      setGameCards(configQuery.data.game_cards && configQuery.data.game_cards.length >= 6 ? configQuery.data.game_cards : defaultCards);
    }
  }, [configQuery.data]);

  const handleUpdateGameCards = (e: React.FormEvent) => {
    e.preventDefault();
    if (!configQuery.data) return;
    updateConfigMutation.mutate(
      {
        config: {
          ...configQuery.data,
          game_cards: gameCards,
        },
        logoSmallFile: null,
        logoBigFile: null,
        faviconFile: null
      },
      {
        onSuccess: () => {
          toast.success('Game Cards updated successfully!');
        },
        onError: (err) => {
          console.error(err);
          toast.error('Failed to update Game Cards');
        }
      }
    );
  };

  const handleCardChange = (cardIndex: number, field: string, value: any) => {
    const updated = [...gameCards];
    updated[cardIndex] = {
      ...updated[cardIndex],
      [field]: value
    };
    setGameCards(updated);
  };

  // ─── TABS 8: AFFILIATE CMS STATE & HANDLERS ───
  const [affiliateOverview, setAffiliateOverview] = useState<any>({ metrics: [], advantages: [] });
  const [affiliateFaqs, setAffiliateFaqs] = useState<any[]>([]);
  const [affiliateTerms, setAffiliateTerms] = useState<string>('');
  const [editingFaqIndex, setEditingFaqIndex] = useState<number | null>(null);
  const [faqQuestion, setFaqQuestion] = useState('');
  const [faqAnswer, setFaqAnswer] = useState('');
  const [faqCategory, setFaqCategory] = useState('General');
  const [isAddingFaq, setIsAddingFaq] = useState(false);

  useEffect(() => {
    if (configQuery.data) {
      setAffiliateOverview(configQuery.data.affiliate_overview || { metrics: [], advantages: [] });
      setAffiliateFaqs(configQuery.data.affiliate_faq || []);
      setAffiliateTerms(configQuery.data.affiliate_terms || '');
    }
  }, [configQuery.data]);

  const handleUpdateAffiliateOverview = (e: React.FormEvent) => {
    e.preventDefault();
    if (!configQuery.data) return;
    updateConfigMutation.mutate(
      {
        config: {
          ...configQuery.data,
          affiliate_overview: affiliateOverview,
        },
        logoSmallFile: null,
        logoBigFile: null,
        faviconFile: null
      },
      {
        onSuccess: () => {
          toast.success('Affiliate Overview settings updated successfully!');
        },
        onError: (err) => {
          console.error(err);
          toast.error('Failed to update settings');
        }
      }
    );
  };

  const handleUpdateAffiliateTerms = (e: React.FormEvent) => {
    e.preventDefault();
    if (!configQuery.data) return;
    updateConfigMutation.mutate(
      {
        config: {
          ...configQuery.data,
          affiliate_terms: affiliateTerms,
        },
        logoSmallFile: null,
        logoBigFile: null,
        faviconFile: null
      },
      {
        onSuccess: () => {
          toast.success('Affiliate Terms & Conditions updated successfully!');
        },
        onError: (err) => {
          console.error(err);
          toast.error('Failed to update terms');
        }
      }
    );
  };

  const handleSaveFaq = (e: React.FormEvent) => {
    e.preventDefault();
    if (!configQuery.data) return;

    let updatedFaqs = [...affiliateFaqs];
    if (isAddingFaq) {
      updatedFaqs.push({ question: faqQuestion, answer: faqAnswer, category: faqCategory });
    } else if (editingFaqIndex !== null) {
      updatedFaqs[editingFaqIndex] = { question: faqQuestion, answer: faqAnswer, category: faqCategory };
    }

    updateConfigMutation.mutate(
      {
        config: {
          ...configQuery.data,
          affiliate_faq: updatedFaqs,
        },
        logoSmallFile: null,
        logoBigFile: null,
        faviconFile: null
      },
      {
        onSuccess: () => {
          toast.success('FAQ saved successfully!');
          setIsAddingFaq(false);
          setEditingFaqIndex(null);
          setFaqQuestion('');
          setFaqAnswer('');
          setFaqCategory('General');
        },
        onError: (err) => {
          console.error(err);
          toast.error('Failed to save FAQ');
        }
      }
    );
  };

  const handleDeleteFaq = (index: number) => {
    if (!window.confirm("Are you sure you want to delete this FAQ?")) return;
    if (!configQuery.data) return;

    const updatedFaqs = affiliateFaqs.filter((_, idx) => idx !== index);

    updateConfigMutation.mutate(
      {
        config: {
          ...configQuery.data,
          affiliate_faq: updatedFaqs,
        },
        logoSmallFile: null,
        logoBigFile: null,
        faviconFile: null
      },
      {
        onSuccess: () => {
          toast.success('FAQ deleted successfully!');
        },
        onError: (err) => {
          console.error(err);
          toast.error('Failed to delete FAQ');
        }
      }
    );
  };

  // Fetch help center pages
  const { data: helpPages = [], isLoading: isHelpLoading, refetch: refetchHelpPages } = useQuery({
    queryKey: ['helpPages'],
    queryFn: async () => {
      const res = await apiClient.get('/admin/help-center', {
        headers: { authorization: localStorage.getItem('token') }
      });
      return res.data?.data || [];
    },
    enabled: activeCmsTab === 'help'
  });

  const handleSaveHelpPage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!helpTitle || !helpSlug || !helpContent) {
      toast.error('Title, Slug and Content are required');
      return;
    }
    const payload = {
      title: helpTitle,
      slug: helpSlug.trim().toLowerCase().replace(/\s+/g, '-'),
      content: helpContent,
      icon_url: helpIconUrl,
      sort_order: helpSortOrder,
      is_active: helpIsActive
    };
    try {
      const url = helpSelectedId ? `/admin/help-center/${helpSelectedId}` : '/admin/help-center';
      await apiClient.post(url, payload, {
        headers: { authorization: localStorage.getItem('token') }
      });
      toast.success('Help Center page saved successfully!');
      refetchHelpPages();
      setHelpIsEditing(false);
      setHelpIsCreatingNew(false);
    } catch (err: any) {
      console.error(err);
      toast.error(err.response?.data?.message || 'Failed to save Help Center page');
    }
  };

  const handleDeleteHelpPage = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this Help Center page?')) return;
    try {
      await apiClient.delete(`/admin/help-center/${id}`, {
        headers: { authorization: localStorage.getItem('token') }
      });
      toast.success('Help Center page deleted successfully!');
      refetchHelpPages();
      setHelpIsEditing(false);
      setHelpIsCreatingNew(false);
    } catch (err: any) {
      console.error(err);
      toast.error(err.response?.data?.message || 'Failed to delete Help Center page');
    }
  };

  const handleEditHelpTabClick = (p: any) => {
    setHelpSelectedId(p.id);
    setHelpTitle(p.title);
    setHelpSlug(p.slug);
    setHelpContent(p.content);
    setHelpIconUrl(p.icon_url || '');
    setHelpSortOrder(p.sort_order || 0);
    setHelpIsActive(p.is_active !== false);
    setHelpIsEditing(true);
    setHelpIsCreatingNew(false);
  };

  const handleCreateNewHelpTabClick = () => {
    setHelpSelectedId(null);
    setHelpTitle('');
    setHelpSlug('');
    setHelpContent('');
    setHelpIconUrl('');
    setHelpSortOrder(0);
    setHelpIsActive(true);
    setHelpIsCreatingNew(true);
    setHelpIsEditing(false);
  };

  const helpColumns: Column[] = useMemo(() => [
    { key: 'title', label: 'Section Title', render: (val: any) => <span className="font-semibold text-white">{val}</span> },
    { key: 'slug', label: 'Slug', render: (val: any) => <span className="text-indigo-400 font-mono">?tab={val}</span> },
    {
      key: 'icon_url',
      label: 'Icon',
      render: (val: any) => val ? (
        <img src={val.startsWith('http') ? val : (val.startsWith('/') ? `${appConfig.frontendURL}${val}` : val)} alt="Icon" className="w-5 h-5 object-contain" />
      ) : '—'
    },
    { key: 'sort_order', label: 'Sort Order', render: (val: any) => val || '0' },
    {
      key: 'is_active',
      label: 'Status',
      render: (val: any) => (
        <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${val !== false
          ? 'bg-green-500/10 text-green-400 border border-green-500/20'
          : 'bg-red-500/10 text-red-400 border border-red-500/20'
          }`}>
          {val !== false ? 'Active' : 'Inactive'}
        </span>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      textAlign: 'right',
      render: (_: any, p: any) => (
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => handleEditHelpTabClick(p)}
            className="bg-indigo-600/10 text-indigo-400 hover:bg-indigo-600 hover:text-white px-3 py-1.5 rounded-md transition text-[10px] font-bold"
          >
            Edit
          </button>
          <button
            type="button"
            onClick={() => handleDeleteHelpPage(p.id)}
            className="bg-red-600/10 text-red-400 hover:bg-red-650 hover:text-white px-3 py-1.5 rounded-md transition text-[10px] font-bold"
          >
            Delete
          </button>
        </div>
      )
    }
  ], [helpPages]);

  const faqColumns: Column[] = useMemo(() => [
    {
      key: 'category',
      label: 'Category',
      width: 'w-36',
      render: (val: any) => (
        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${val === 'General' ? 'bg-sky-500/10 text-sky-400 border border-sky-500/20' :
          val === 'Affiliate Program' ? 'bg-purple-500/10 text-purple-400 border border-purple-500/20' :
            'bg-amber-500/10 text-amber-400 border border-amber-500/20'
          }`}>
          {val || 'General'}
        </span>
      )
    },
    { key: 'question', label: 'Question', render: (val: any) => <span className="font-semibold text-white">{val}</span> },
    { key: 'answer', label: 'Answer', render: (val: any) => <span className="text-gray-400 text-xs block max-w-sm truncate">{val}</span> },
    {
      key: 'actions',
      label: 'Actions',
      textAlign: 'right',
      render: (_: any, faq: any, index: number) => (
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => {
              setEditingFaqIndex(index);
              setIsAddingFaq(false);
              setFaqQuestion(faq.question);
              setFaqAnswer(faq.answer);
              setFaqCategory(faq.category || 'General');
            }}
            className="bg-indigo-600/10 text-indigo-400 hover:bg-indigo-600 hover:text-white px-2.5 py-1.5 rounded-md transition text-[10px] font-bold"
          >
            Edit
          </button>
          <button
            type="button"
            onClick={() => handleDeleteFaq(index)}
            className="bg-red-600/10 text-red-400 hover:bg-red-650 hover:text-white px-2.5 py-1.5 rounded-md transition text-[10px] font-bold"
          >
            Delete
          </button>
        </div>
      )
    }
  ], [affiliateFaqs]);

  const handleUploadSocialIconFile = async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('social_icon', file);
    const res = await apiClient.post('/admin/upload-social-icon', formData, {
      headers: {
        authorization: localStorage.getItem('token') || '',
      }
    });
    return res.data?.url || '';
  };

  const handleSaveSocialLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingSocialLink) return;

    let iconUrl = editingSocialLink.icon_url || '';
    if (socialFile) {
      try {
        iconUrl = await handleUploadSocialIconFile(socialFile);
      } catch (err) {
        toast.error("Failed to upload icon image");
        return;
      }
    }

    const updatedLink = {
      ...editingSocialLink,
      icon_url: iconUrl,
      id: editingSocialLink.id || `social-${Date.now()}`
    };

    let updatedLinksList = [];
    if (isAddingSocialLink) {
      updatedLinksList = [...socialLinks, updatedLink];
    } else {
      updatedLinksList = socialLinks.map(s => s.id === editingSocialLink.id ? updatedLink : s);
    }

    // Save to server
    updateConfigMutation.mutate(
      {
        config: {
          ...configQuery.data!,
          social_links: updatedLinksList,
        },
        logoSmallFile: null,
        logoBigFile: null,
        faviconFile: null
      },
      {
        onSuccess: () => {
          toast.success('Social link saved successfully!');
          setEditingSocialLink(null);
          setIsAddingSocialLink(false);
          setSocialFile(null);
        },
        onError: (err) => {
          console.error(err);
          toast.error('Failed to save social link');
        }
      }
    );
  };

  const handleDeleteSocialLink = (id: string) => {
    if (!window.confirm("Are you sure you want to delete this social link?")) return;

    const updatedLinksList = socialLinks.filter(s => s.id !== id);

    updateConfigMutation.mutate(
      {
        config: {
          ...configQuery.data!,
          social_links: updatedLinksList,
        },
        logoSmallFile: null,
        logoBigFile: null,
        faviconFile: null
      },
      {
        onSuccess: () => {
          toast.success('Social link deleted successfully!');
        },
        onError: (err) => {
          console.error(err);
          toast.error('Failed to delete social link');
        }
      }
    );
  };

  const handleToggleSocialLink = (id: string, field: 'is_enabled' | 'show_in_footer' | 'show_in_sidebar') => {
    const updatedLinksList = socialLinks.map(s => s.id === id ? { ...s, [field]: !s[field] } : s);

    updateConfigMutation.mutate(
      {
        config: {
          ...configQuery.data!,
          social_links: updatedLinksList,
        },
        logoSmallFile: null,
        logoBigFile: null,
        faviconFile: null
      },
      {
        onSuccess: () => {
          toast.success('Status updated successfully!');
        },
        onError: (err) => {
          console.error(err);
          toast.error('Failed to update status');
        }
      }
    );
  };

  // Fetch all CMS pages
  const { data: pages = [], isLoading: isCmsLoading } = useQuery<CmsPage[]>({
    queryKey: ['cmsPages'],
    queryFn: async () => {
      const res = await apiClient.get('/admin/cms-pages', {
        headers: { authorization: localStorage.getItem('token') }
      });
      return res.data?.data || [];
    },
    retry: false
  });

  // Sync selected page data to local state
  useEffect(() => {
    if (pages.length > 0 && isEditing) {
      const currentPage = pages.find((p: CmsPage) => p.key === selectedKey);
      if (currentPage) {
        setTitle(currentPage.title || '');
        setContent(currentPage.content || '');
        setMetaTitle(currentPage.meta_title || '');
        setMetaDescription(currentPage.meta_description || '');
        setIsActive(currentPage.is_active !== false);
      }
    }
  }, [selectedKey, pages, isEditing]);

  // Setup form for creating a new page
  const handleCreateNewTabClick = () => {
    setIsCreatingNew(true);
    setIsEditing(false);
    setSelectedKey('');
    setKeyInput('');
    setTitle('');
    setContent('');
    setMetaTitle('');
    setMetaDescription('');
    setIsActive(true);
  };

  const handlePageTabClick = (key: string) => {
    setIsCreatingNew(false);
    setIsEditing(true);
    setSelectedKey(key);
  };

  // Jodit Editor configuration
  const joditConfig = useMemo(() => ({
    readonly: false,
    theme: 'dark',
    placeholder: 'Start writing premium content...',
    askBeforePasteHTML: false,
    askBeforePasteFromWord: false,
    defaultActionOnPaste: 'insert_clear_html',
    beautyHTML: true,
    height: 400,
    buttons: [
      'source', '|',
      'bold', 'italic', 'underline', 'strikethrough', '|',
      'font', 'fontsize', 'brush', 'paragraph', '|',
      'table', 'link', 'image', 'video', '|',
      'align', 'ul', 'ol', '|',
      'outdent', 'indent', '|',
      'hr', 'eraser', 'copyformat', '|',
      'undo', 'redo', '|',
      'fullsize'
    ],
    uploader: {
      insertImageAsBase64URI: true
    }
  }), []);

  // Save/Update page mutation
  const saveMutation = useMutation({
    mutationFn: async (payload: { key: string; data: Partial<CmsPage> }) => {
      const res = await apiClient.put(`/admin/cms-pages/${payload.key}`, payload.data, {
        headers: { authorization: localStorage.getItem('token') }
      });
      return res.data;
    },
    onSuccess: (data: any, variables: any) => {
      toast.success('Page saved successfully!');
      queryClient.invalidateQueries({ queryKey: ['cmsPages'] });
      setIsCreatingNew(false);
      setIsEditing(false);
    },
    onError: (err: any) => {
      console.error(err);
      toast.error(err.response?.data?.message || 'Failed to save page');
    }
  });

  // Delete page mutation
  const deletePageMutation = useMutation({
    mutationFn: async (key: string) => {
      const res = await apiClient.delete(`/admin/cms-pages/${key}`, {
        headers: { authorization: localStorage.getItem('token') }
      });
      return res.data;
    },
    onSuccess: () => {
      toast.success('Page deleted successfully!');
      queryClient.invalidateQueries({ queryKey: ['cmsPages'] });
      setIsCreatingNew(false);
      setIsEditing(false);
    },
    onError: (err: any) => {
      console.error(err);
      toast.error(err.response?.data?.message || 'Failed to delete page');
    }
  });

  const handleSavePage = (e: React.FormEvent) => {
    e.preventDefault();
    const saveKey = isCreatingNew ? keyInput.trim().toLowerCase().replace(/\s+/g, '_') : selectedKey;

    if (!saveKey) {
      toast.error('Page Key is required');
      return;
    }

    saveMutation.mutate({
      key: saveKey,
      data: {
        title,
        content,
        meta_title: metaTitle,
        meta_description: metaDescription,
        is_active: isActive
      }
    });
  };

  const handleDeletePage = (key: string) => {
    if (window.confirm(`Are you sure you want to delete page: ${key}?`)) {
      deletePageMutation.mutate(key);
    }
  };

  // ─── TABS 2: CORE BRANDING CONTROLS ───
  const handleUpdateConfig = () => {
    if (!brandConfig) return;
    updateConfigMutation.mutate(
      { config: brandConfig, logoSmallFile, logoBigFile, faviconFile, appFile },
      {
        onSuccess: () => {
          toast.success('Logos and site config updated!');
          setLogoSmallFile(null);
          setLogoBigFile(null);
          setFaviconFile(null);
          setAppFile(null);
        },
        onError: (err) => {
          console.error(err);
          toast.error('Error updating branding settings');
        }
      }
    );
  };

  // ─── TABS 3: BANNER MANAGEMENT CONTROLS ───
  const handleAddBanner = (e: React.FormEvent) => {
    e.preventDefault();
    if (newBanner.type === 'home') {
      handleUpdateConfig();
    }
    if (!bannerFile) return toast.warn("Please select an image");

    addBannerMutation.mutate(
      { newBanner: { ...newBanner, theme: activeDesign }, bannerFile },
      {
        onSuccess: () => {
          toast.success('Banner uploaded successfully!');
          setBannerFile(null);
          setBannerLocalPreview(null);
          setIsAddingBanner(false);
          setNewBanner({
            type: 'home',
            link_url: '',
            sort_order: 0,
            tag: '',
            heading: '',
            subheading: '',
            button_text: '',
            button_link: '',
            gradient: '',
            theme: activeDesign,
            is_active: true
          });
        },
        onError: (err: any) => {
          console.error(err);
          toast.error(err?.message || 'Error uploading banner');
        }
      }
    );
  };

  const handleUpdateBanner = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingBanner && editingBanner.type === 'home') {
      handleUpdateConfig();
    }
    if (!editingBanner) return;

    updateBannerMutation.mutate(
      { id: editingBanner.id, updatedBanner: { ...editingBanner, theme: activeDesign }, bannerFile: editBannerFile },
      {
        onSuccess: () => {
          toast.success('Banner updated successfully!');
          setEditingBanner(null);
          setEditBannerFile(null);
          setEditBannerLocalPreview(null);
        },
        onError: (err: any) => {
          console.error(err);
          toast.error(err?.message || 'Error updating banner');
        }
      }
    );
  };


  const cmsColumns: Column[] = useMemo(() => [
    { key: 'title', label: 'Page Title', render: (val: any) => <span className="font-semibold text-white">{val}</span> },
    { key: 'key', label: 'Slug / Route', render: (val: any) => <span className="text-indigo-400 font-mono">/{val}</span> },
    { key: 'meta_title', label: 'SEO Title', render: (val: any) => val || '—' },
    {
      key: 'is_active',
      label: 'Status',
      render: (val: any) => (
        <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${val !== false
          ? 'bg-green-500/10 text-green-400 border border-green-500/20'
          : 'bg-red-500/10 text-red-400 border border-red-500/20'
          }`}>
          {val !== false ? 'Active' : 'Inactive'}
        </span>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      textAlign: 'right',
      render: (_: any, p: any) => (
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => handlePageTabClick(p.key)}
            className="bg-indigo-600/10 text-indigo-400 hover:bg-indigo-600 hover:text-white px-3 py-1.5 rounded-md transition text-[10px] font-bold"
          >
            Edit
          </button>
          <button
            type="button"
            onClick={() => handleDeletePage(p.key)}
            disabled={deletePageMutation.isPending}
            className="bg-red-600/10 text-red-400 hover:bg-red-650 hover:text-white px-3 py-1.5 rounded-md transition text-[10px] font-bold"
          >
            Delete
          </button>
        </div>
      )
    }
  ], [pages, deletePageMutation.isPending]);

  const bannerColumns: Column[] = useMemo(() => {
    const cols: Column[] = [
      {
        key: 'image_url',
        label: 'Preview',
        render: (val: any) => (
          <img src={`${appConfig.baseURL}${val}`} alt="Banner" className="h-8 w-20 object-cover rounded bg-gray-950 border border-slate-700" />
        )
      },
      { key: 'theme', label: 'Theme', render: (val: any) => <span className="font-semibold uppercase text-slate-300">{val || 'bc'}</span> },
      { key: 'type', label: 'Location', render: (val: any) => <span className="font-semibold uppercase text-slate-300">{val}</span> },
    ];

    if (activeDesign === 'stake') {
      cols.push({ key: 'heading', label: 'Heading / Title', render: (val: any) => <span>{val || '—'}</span> });
    }

    cols.push(
      { key: 'link_url', label: 'Redirection', render: (val: any) => val ? <span className="text-indigo-400">{val}</span> : <span>—</span> },
      {
        key: 'is_active',
        label: 'Status',
        render: (val: any, b: any) => (
          <label className="inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={!!val}
              onChange={() => toggleBannerMutation.mutate(b.id)}
              disabled={toggleBannerMutation.isPending}
              className="sr-only peer"
            />
            <div className="relative w-8 h-4 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-indigo-600"></div>
            <span className="ms-2 text-[10px] font-bold text-slate-400 uppercase tracking-wider">{val ? 'Active' : 'Hidden'}</span>
          </label>
        )
      },
      {
        key: 'actions',
        label: 'Actions',
        textAlign: 'right',
        render: (_: any, b: any) => (
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setEditingBanner({
                  id: b.id,
                  type: b.type,
                  link_url: b.link_url || '',
                  sort_order: b.sort_order || 0,
                  is_active: b.is_active,
                  tag: b.tag || '',
                  heading: b.heading || '',
                  subheading: b.subheading || '',
                  button_text: b.button_text || '',
                  button_link: b.button_link || '',
                  gradient: b.gradient || '',
                  theme: b.theme || 'bc',
                  image_url: b.image_url || ''
                });
                setEditBannerFile(null);
                setEditBannerLocalPreview(null);
              }}
              className="bg-indigo-600/10 text-indigo-400 hover:bg-indigo-600 hover:text-white px-2.5 py-1.5 rounded-md transition text-[10px] font-bold"
            >
              Edit
            </button>
            <button
              type="button"
              onClick={() => { if (window.confirm("Delete this banner?")) deleteBannerMutation.mutate(b.id) }}
              disabled={deleteBannerMutation.isPending}
              className="bg-red-600/10 text-red-400 hover:bg-red-650 hover:text-white px-2.5 py-1.5 rounded-md transition text-[10px] font-bold"
            >
              Delete
            </button>
          </div>
        )
      }
    );

    return cols;
  }, [activeDesign, toggleBannerMutation.isPending, deleteBannerMutation.isPending]);

  return (
    <div className="ml-0 lg:ml-[256px] text-white">
      <JoditThemeOverride />
      {activeCmsTab === 'pages' && (
        <>
          <h1 className="text-3xl font-bold mb-8 text-white">
            {isCreatingNew ? 'Create CMS Page' : isEditing ? 'Edit CMS Page' : 'CMS Pages'}
          </h1>

          {!isCreatingNew && !isEditing ? (
            <div className="mb-6">
              <StakeTable
                title=""
                columns={cmsColumns}
                data={pages.filter((p: CmsPage) =>
                  p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  p.key.toLowerCase().includes(searchQuery.toLowerCase())
                )}
                searchPlaceholder="Search pages by title or slug..."
                onSearch={(val) => setSearchQuery(val)}
                emptyMessage="No pages found."
                hasSidebarMargin={false}
                isLoading={isCmsLoading}
                actions={
                  <button
                    type="button"
                    onClick={handleCreateNewTabClick}
                    className="bg-green-600 hover:bg-green-700 text-white text-xs font-bold py-2 px-3 rounded-md flex items-center gap-2 transition-colors shadow-sm whitespace-nowrap"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Create New Page
                  </button>
                }
              />
            </div>
          ) : (
            /* Main Form Editor Area */
            <div className="bg-slate-900/60 p-4 sm:p-6 rounded-xl border border-gray-800 shadow-xl h-fit">
              <form onSubmit={handleSavePage} className="space-y-4">
                <div className="flex justify-between items-center border-b border-gray-800 pb-3">
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => { setIsCreatingNew(false); setIsEditing(false); }}
                      className="text-gray-400 hover:text-white text-xs font-bold flex items-center gap-1 bg-slate-800/50 hover:bg-slate-800 px-3 py-1.5 rounded-lg border border-gray-750 transition-all"
                    >
                      <i className="fas fa-arrow-left"></i> Back to list
                    </button>
                    <h3 className="text-xs font-black uppercase text-white tracking-wide">
                      {isCreatingNew ? 'Create New Page Setup' : `Edit Content: ${selectedKey}`}
                    </h3>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="page-active"
                      checked={isActive}
                      onChange={(e) => setIsActive(e.target.checked)}
                      className="rounded border-gray-700 bg-gray-800 accent-indigo-500 w-3.5 h-3.5"
                    />
                    <label htmlFor="page-active" className="text-xs font-bold text-gray-300 select-none">
                      Active
                    </label>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {isCreatingNew && (
                    <div>
                      <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">
                        Page Key (Slug)
                      </label>
                      <input
                        type="text"
                        required
                        value={keyInput}
                        onChange={(e) => setKeyInput(e.target.value)}
                        className="w-full bg-slate-800/60 border border-gray-700 text-white rounded-lg px-3 py-2 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        placeholder="e.g. refund_policy"
                      />
                    </div>
                  )}
                  <div className={isCreatingNew ? 'md:col-span-1' : 'md:col-span-2'}>
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">
                      Display Title
                    </label>
                    <input
                      type="text"
                      required
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      className="w-full bg-slate-800/60 border border-gray-700 text-white rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500"
                      placeholder="e.g. Privacy Policy"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">
                      SEO Title
                    </label>
                    <input
                      type="text"
                      value={metaTitle}
                      onChange={(e) => setMetaTitle(e.target.value)}
                      className="w-full bg-slate-800/60 border border-gray-700 text-white rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500"
                      placeholder="Meta title for Google search"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">
                    SEO Description
                  </label>
                  <textarea
                    rows={1}
                    value={metaDescription}
                    onChange={(e) => setMetaDescription(e.target.value)}
                    className="w-full bg-slate-800/60 border border-gray-700 text-white rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    placeholder="Short meta description"
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">
                    Page Content
                  </label>
                  <div className="bg-slate-800 rounded-lg border border-gray-700 text-white overflow-hidden jodit-dark-container">
                    <JoditEditor
                      ref={editorRef}
                      value={content}
                      config={joditConfig as any}
                      onBlur={newContent => setContent(newContent)}
                      onChange={newContent => { }}
                    />
                  </div>
                </div>

                <div className="flex justify-between gap-3 pt-2">
                  <button
                    type="submit"
                    disabled={saveMutation.isPending}
                    className="flex-1 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-xs font-bold py-2.5 px-4 rounded-lg transition-colors shadow-lg"
                  >
                    {saveMutation.isPending ? 'Saving...' : 'Save Page Content'}
                  </button>
                  {!isCreatingNew && (
                    <button
                      type="button"
                      onClick={() => handleDeletePage(selectedKey)}
                      disabled={deletePageMutation.isPending}
                      className="px-5 py-2.5 bg-red-600/20 text-red-400 hover:bg-red-650 hover:text-white rounded-lg text-xs font-bold transition-all"
                    >
                      Delete Page
                    </button>
                  )}
                </div>
              </form>
            </div>
          )}
        </>
      )}

      {activeCmsTab === 'banners' && (
        <>
          <style>{`
            .no-scrollbar::-webkit-scrollbar {
              display: none;
            }
          `}</style>



          {editingBanner && (
            <div className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
              <div
                className="bg-slate-900 text-white rounded-xl max-w-2xl w-full p-5 border border-gray-800 shadow-2xl overflow-y-auto max-h-[80vh] no-scrollbar"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              >
                <div className="flex justify-between items-center border-b border-gray-800 pb-3 mb-4">
                  <h3 className="text-indigo-400 text-xs font-black uppercase tracking-wider">Edit Slide Banner</h3>
                  <button
                    type="button"
                    onClick={() => { setEditingBanner(null); setEditBannerFile(null); setEditBannerLocalPreview(null); }}
                    className="text-gray-400 hover:text-white text-xl font-bold leading-none"
                  >
                    &times;
                  </button>
                </div>

                <form onSubmit={handleUpdateBanner} className="grid grid-cols-1 sm:grid-cols-2 gap-4">

                  <div>
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Banner Type</label>
                    <select
                      value={editingBanner.type}
                      onChange={(e) => setEditingBanner({ ...editingBanner, type: e.target.value })}
                      className="w-full bg-slate-800/60 text-white rounded-lg p-2.5 text-xs border border-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    >
                      <option value="home">Homepage Cards</option>
                      <option value="casino">Casino Header Slider</option>
                      <option value="sports">Sports Header Slider</option>
                      <option value="promotions">Promotions Page</option>
                      <option value="missions">Missions Page</option>
                      <option value="offers">Offers Page</option>
                      <option value="sidebar">Sidebar Promo</option>
                    </select>
                  </div>

                  {activeDesign === 'stake' && !isSimpleBannerType(editingBanner.type) && (
                    <>
                      {editingBanner.type === 'home' ? (
                        <div className="sm:col-span-2 bg-slate-950/40 p-4 rounded-xl border border-slate-800/80">
                          <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1">
                            <span className="text-indigo-400">★</span> Select Icon (for Homepage Category Card)
                          </label>
                          <div className="grid grid-cols-5 sm:grid-cols-8 gap-2 p-2.5 bg-slate-950/50 border border-slate-700/50 rounded-lg max-h-[140px] overflow-y-auto">
                            {["Trophy", "Crown", "Star", "Flame", "Gift", "Coins", "Gem", "Target", "Zap", "Swords", "Shield", "Dice5", "Gamepad2", "Heart", "Sparkles", "Rocket", "Globe", "TrendingUp", "BarChart2", "DollarSign", "Percent", "Award", "Medal", "Ticket", "CircleDollarSign", "Banknote", "Landmark", "Wallet", "CreditCard", "ChartBar", "Activity", "Timer", "Clock", "Play", "Users", "UserCheck", "Lock", "Unlock", "Key", "Bitcoin"].map((item) => {
                              const IconComponent = (LucideIcons as any)[item] || LucideIcons.Star;
                              if (!IconComponent) return null;
                              const isSelected = editingBanner.tag === item;
                              return (
                                <button
                                  key={item}
                                  type="button"
                                  onClick={() => setEditingBanner({ ...editingBanner, tag: item })}
                                  className={`p-2 rounded-lg flex flex-col items-center justify-center gap-1 transition-all ${isSelected
                                    ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/30 scale-105'
                                    : 'bg-slate-900 border border-slate-800 text-slate-400 hover:border-slate-600 hover:text-white'
                                    }`}
                                >
                                  <IconComponent size={18} />
                                  <span className="text-[8px] truncate max-w-full font-bold">{item}</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ) : (
                        <div>
                          <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Tag Badge</label>
                          <input
                            type="text"
                            value={editingBanner.tag || ''}
                            onChange={(e) => setEditingBanner({ ...editingBanner, tag: e.target.value })}
                            className="w-full bg-slate-800/60 text-white rounded-lg p-2.5 text-xs border border-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                          />
                        </div>
                      )}

                      {/* Global Hero Text — same for ALL homepage cards */}
                      {editingBanner.type === 'home' && brandConfig && (
                        <div className="col-span-1 sm:col-span-2 bg-indigo-950/30 border border-indigo-800/40 rounded-lg p-3 space-y-3">
                          <p className="text-[10px] font-black text-indigo-400 uppercase tracking-wider flex items-center gap-1.5">
                            <LucideIcons.Globe size={11} /> Global Homepage Hero Text (applies to all cards)
                          </p>
                          <div>
                            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Hero Title (Logged-Out Banner)</label>
                            <input
                              type="text"
                              value={brandConfig.home_hero_title || ''}
                              onChange={(e) => setBrandConfig({ ...brandConfig, home_hero_title: e.target.value })}
                              className="w-full bg-slate-800/60 text-white rounded-lg p-2.5 text-xs border border-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                              placeholder="e.g. The World's #1 Casino & Sportsbook"
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Hero Subtitle (Logged-Out Banner)</label>
                            <input
                              type="text"
                              value={brandConfig.home_hero_subtitle || ''}
                              onChange={(e) => setBrandConfig({ ...brandConfig, home_hero_subtitle: e.target.value })}
                              className="w-full bg-slate-800/60 text-white rounded-lg p-2.5 text-xs border border-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                              placeholder="e.g. Experience world-class gaming with exclusive rewards."
                            />
                          </div>
                        </div>
                      )}

                      <div>
                        <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Heading / Title</label>
                        <input
                          type="text"
                          value={editingBanner.heading || ''}
                          onChange={(e) => setEditingBanner({ ...editingBanner, heading: e.target.value })}
                          className="w-full bg-slate-800/60 text-white rounded-lg p-2.5 text-xs border border-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        />
                      </div>

                      <div>
                        <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Subheading / Description</label>
                        <input
                          type="text"
                          value={editingBanner.subheading || ''}
                          onChange={(e) => setEditingBanner({ ...editingBanner, subheading: e.target.value })}
                          className="w-full bg-slate-800/60 text-white rounded-lg p-2.5 text-xs border border-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        />
                      </div>


                      {editingBanner.type !== 'home' && (
                        <>
                          <div>
                            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Button Text</label>
                            <input
                              type="text"
                              value={editingBanner.button_text || ''}
                              onChange={(e) => setEditingBanner({ ...editingBanner, button_text: e.target.value })}
                              className="w-full bg-slate-800/60 text-white rounded-lg p-2.5 text-xs border border-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                            />
                          </div>

                          <div>
                            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Button Link URL</label>
                            <input
                              type="text"
                              value={editingBanner.button_link || ''}
                              onChange={(e) => setEditingBanner({ ...editingBanner, button_link: e.target.value })}
                              className="w-full bg-slate-800/60 text-white rounded-lg p-2.5 text-xs border border-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                            />
                          </div>

                          <div>
                            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Background Gradient</label>
                            <input
                              type="text"
                              value={editingBanner.gradient || ''}
                              onChange={(e) => setEditingBanner({ ...editingBanner, gradient: e.target.value })}
                              className="w-full bg-slate-800/60 text-white rounded-lg p-2.5 text-xs border border-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                            />
                          </div>
                        </>
                      )}
                    </>
                  )}

                  <div>
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">
                      {editingBanner.type === 'home' ? 'Redirection Link URL (e.g. /casino)' : 'Link URL (click fallback)'}
                    </label>
                    <input
                      type="text"
                      value={editingBanner.link_url}
                      onChange={(e) => setEditingBanner({ ...editingBanner, link_url: e.target.value })}
                      className="w-full bg-slate-800/60 text-white rounded-lg p-2.5 text-xs border border-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                      placeholder={editingBanner.type === 'home' ? 'e.g. /casino' : '/casino/slots'}
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Sort Order</label>
                    <input
                      type="number"
                      value={editingBanner.sort_order}
                      onChange={(e) => setEditingBanner({ ...editingBanner, sort_order: parseInt(e.target.value) || 0 })}
                      className="w-full bg-slate-800/60 text-white rounded-lg p-2.5 text-xs border border-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    />
                  </div>

                  <div className="flex items-center pt-5">
                    <label className="inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!editingBanner.is_active}
                        onChange={(e) => setEditingBanner({ ...editingBanner, is_active: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="relative w-9 h-5 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-600"></div>
                      <span className="ms-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider">Active Status</span>
                    </label>
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">
                      Banner Image {isSimpleBannerType(editingBanner.type) ? '(Required)' : '(Optional Replace)'}
                    </label>

                    <input
                      ref={editBannerFileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleBannerFileSelect(file, 'edit');
                        e.target.value = '';
                      }}
                      className="hidden"
                    />

                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 p-3 bg-slate-950/50 rounded-xl border border-slate-800">
                      <div className="relative w-full sm:w-44 h-24 rounded-lg overflow-hidden bg-slate-900 border border-slate-700 flex items-center justify-center flex-shrink-0">
                        {editBannerLocalPreview ? (
                          <img src={editBannerLocalPreview} alt="Preview" className="w-full h-full object-cover" />
                        ) : editingBanner.image_url ? (
                          <img src={`${appConfig.baseURL}${editingBanner.image_url}`} alt="Current Banner" className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-[10px] text-slate-500 font-bold uppercase">No Image</span>
                        )}
                        {editBannerLocalPreview && (
                          <span className="absolute bottom-1 right-1 bg-green-500/90 text-white text-[9px] font-bold px-1.5 py-0.5 rounded shadow">
                            Cropped
                          </span>
                        )}
                      </div>

                      <div className="space-y-2 flex-1">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => editBannerFileInputRef.current?.click()}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded-lg text-xs font-bold transition flex items-center gap-1.5 shadow"
                          >
                            <LucideIcons.Crop size={14} />
                            {editBannerLocalPreview || editingBanner.image_url ? 'Choose & Crop New Image' : 'Select & Crop Image'}
                          </button>
                          {editBannerLocalPreview && (
                            <button
                              type="button"
                              onClick={() => {
                                setEditBannerFile(null);
                                setEditBannerLocalPreview(null);
                              }}
                              className="bg-red-600/10 text-red-400 hover:bg-red-600 hover:text-white px-2.5 py-1.5 rounded-lg text-xs font-bold transition"
                            >
                              Revert
                            </button>
                          )}
                        </div>
                        <p className="text-[10px] text-slate-400">
                          {editBannerLocalPreview
                            ? '✓ Cropped locally. Image will be uploaded to backend only when you click "Save Changes".'
                            : 'Recommended 16:9 banner aspect ratio. Cropping tool opens upon image selection.'}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="sm:col-span-2 pt-4 flex gap-3 border-t border-gray-800 mt-2">
                    <button type="submit" disabled={updateBannerMutation.isPending} className="flex-1 bg-indigo-600 disabled:opacity-50 text-white py-2.5 rounded-lg text-xs font-bold hover:bg-indigo-700 transition shadow">
                      {updateBannerMutation.isPending ? 'Saving...' : 'Save Changes'}
                    </button>
                    <button type="button" onClick={() => { setEditingBanner(null); setEditBannerFile(null); setEditBannerLocalPreview(null); }} className="flex-1 bg-slate-800 text-gray-300 py-2.5 rounded-lg text-xs font-bold border border-slate-700 hover:border-slate-600 hover:bg-slate-750 transition">
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* ADD BANNER MODAL */}
          {isAddingBanner && (
            <div className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
              <div
                className="bg-slate-900 text-white rounded-xl max-w-2xl w-full p-5 border border-gray-800 shadow-2xl overflow-y-auto max-h-[80vh] no-scrollbar"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              >
                <div className="flex justify-between items-center border-b border-gray-800 pb-3 mb-4">
                  <h3 className="text-indigo-400 text-xs font-black uppercase tracking-wider">Upload New Slide Banner</h3>
                  <button
                    type="button"
                    onClick={() => { setIsAddingBanner(false); setBannerFile(null); setBannerLocalPreview(null); }}
                    className="text-gray-400 hover:text-white text-xl font-bold leading-none"
                  >
                    &times;
                  </button>
                </div>

                <form onSubmit={handleAddBanner} className="grid grid-cols-1 sm:grid-cols-2 gap-4">

                  <div>
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Banner Type</label>
                    <select
                      value={newBanner.type}
                      onChange={(e) => setNewBanner({ ...newBanner, type: e.target.value })}
                      className="w-full bg-slate-800/60 text-white rounded-lg p-2.5 text-xs border border-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    >
                      <option value="home">Homepage Cards</option>
                      <option value="casino">Casino Header Slider</option>
                      <option value="sports">Sports Header Slider</option>
                      <option value="promotions">Promotions Page</option>
                      <option value="missions">Missions Page</option>
                      <option value="offers">Offers Page</option>
                      <option value="sidebar">Sidebar Promo</option>
                    </select>
                  </div>

                  {activeDesign === 'stake' && !isSimpleBannerType(newBanner.type) && (
                    <>
                      {newBanner.type === 'home' ? (
                        <div className="col-span-1 sm:col-span-2">
                          <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Select Icon (for Homepage Category Card)</label>
                          <div className="grid grid-cols-5 sm:grid-cols-8 gap-2 p-2.5 bg-slate-950/50 border border-slate-700/50 rounded-lg max-h-[140px] overflow-y-auto">
                            {["Trophy", "Crown", "Star", "Flame", "Gift", "Coins", "Gem", "Target", "Zap", "Swords", "Shield", "Dice5", "Gamepad2", "Heart", "Sparkles", "Rocket", "Globe", "TrendingUp", "BarChart2", "DollarSign", "Percent", "Award", "Medal", "Ticket", "CircleDollarSign", "Banknote", "Landmark", "Wallet", "CreditCard", "Activity", "Timer", "Clock", "Play", "Users", "UserCheck", "Lock", "Unlock", "Key", "Bitcoin"].map((item) => {
                              const IconComponent = (LucideIcons as any)[item] || LucideIcons.Globe;
                              const isSelected = newBanner.tag === item;
                              return (
                                <button
                                  key={item}
                                  type="button"
                                  onClick={() => setNewBanner({ ...newBanner, tag: item })}
                                  className={`p-2 rounded-lg flex flex-col items-center justify-center gap-1 transition-all ${isSelected
                                    ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/30 scale-105'
                                    : 'bg-slate-900 border border-slate-800 text-slate-400 hover:border-slate-600 hover:text-white'
                                    }`}
                                >
                                  <IconComponent size={18} />
                                  <span className="text-[8px] truncate max-w-full font-bold">{item}</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ) : (
                        <div>
                          <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Tag Badge</label>
                          <input
                            type="text"
                            value={newBanner.tag || ''}
                            onChange={(e) => setNewBanner({ ...newBanner, tag: e.target.value })}
                            className="w-full bg-slate-800/60 text-white rounded-lg p-2.5 text-xs border border-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                            placeholder="e.g. Only on SevenX"
                          />
                        </div>
                      )}

                      {/* Global Hero Text — same for ALL homepage cards */}
                      {newBanner.type === 'home' && brandConfig && (
                        <div className="col-span-1 sm:col-span-2 bg-indigo-950/30 border border-indigo-800/40 rounded-lg p-3 space-y-3">
                          <p className="text-[10px] font-black text-indigo-400 uppercase tracking-wider flex items-center gap-1.5">
                            <LucideIcons.Globe size={11} /> Global Homepage Hero Text (applies to all cards)
                          </p>
                          <div>
                            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Hero Title (Logged-Out Banner)</label>
                            <input
                              type="text"
                              value={brandConfig.home_hero_title || ''}
                              onChange={(e) => setBrandConfig({ ...brandConfig, home_hero_title: e.target.value })}
                              className="w-full bg-slate-800/60 text-white rounded-lg p-2.5 text-xs border border-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                              placeholder="e.g. The World's #1 Casino & Sportsbook"
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Hero Subtitle (Logged-Out Banner)</label>
                            <input
                              type="text"
                              value={brandConfig.home_hero_subtitle || ''}
                              onChange={(e) => setBrandConfig({ ...brandConfig, home_hero_subtitle: e.target.value })}
                              className="w-full bg-slate-800/60 text-white rounded-lg p-2.5 text-xs border border-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                              placeholder="e.g. Experience world-class gaming with exclusive rewards."
                            />
                          </div>
                        </div>
                      )}

                      <div>
                        <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Heading / Title</label>
                        <input
                          type="text"
                          value={newBanner.heading || ''}
                          onChange={(e) => setNewBanner({ ...newBanner, heading: e.target.value })}
                          className="w-full bg-slate-800/60 text-white rounded-lg p-2.5 text-xs border border-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                          placeholder="e.g. Mountie Bounties"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Subheading / Description</label>
                        <input
                          type="text"
                          value={newBanner.subheading || ''}
                          onChange={(e) => setNewBanner({ ...newBanner, subheading: e.target.value })}
                          className="w-full bg-slate-800/60 text-white rounded-lg p-2.5 text-xs border border-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                          placeholder="e.g. Wanted: Big Wins"
                        />
                      </div>

                      {newBanner.type !== 'home' && (
                        <>
                          <div>
                            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Button Text</label>
                            <input
                              type="text"
                              value={newBanner.button_text || ''}
                              onChange={(e) => setNewBanner({ ...newBanner, button_text: e.target.value })}
                              className="w-full bg-slate-800/60 text-white rounded-lg p-2.5 text-xs border border-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                              placeholder="e.g. Play Now!"
                            />
                          </div>

                          <div>
                            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Button Link URL</label>
                            <input
                              type="text"
                              value={newBanner.button_link || ''}
                              onChange={(e) => setNewBanner({ ...newBanner, button_link: e.target.value })}
                              className="w-full bg-slate-800/60 text-white rounded-lg p-2.5 text-xs border border-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                              placeholder="e.g. /play/wanted"
                            />
                          </div>

                          <div>
                            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Background Gradient</label>
                            <input
                              type="text"
                              value={newBanner.gradient || ''}
                              onChange={(e) => setNewBanner({ ...newBanner, gradient: e.target.value })}
                              className="w-full bg-slate-800/60 text-white rounded-lg p-2.5 text-xs border border-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                              placeholder="e.g. linear-gradient(...)"
                            />
                          </div>
                        </>
                      )}
                    </>
                  )}

                  <div>
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">
                      {newBanner.type === 'home' ? 'Redirection Link URL (e.g. /casino)' : 'Link URL (click fallback)'}
                    </label>
                    <input
                      type="text"
                      value={newBanner.link_url}
                      onChange={(e) => setNewBanner({ ...newBanner, link_url: e.target.value })}
                      className="w-full bg-slate-800/60 text-white rounded-lg p-2.5 text-xs border border-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                      placeholder={newBanner.type === 'home' ? 'e.g. /casino' : '/casino/slots'}
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Sort Order</label>
                    <input
                      type="number"
                      value={newBanner.sort_order}
                      onChange={(e) => setNewBanner({ ...newBanner, sort_order: parseInt(e.target.value) || 0 })}
                      className="w-full bg-slate-800/60 text-white rounded-lg p-2.5 text-xs border border-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    />
                  </div>

                  <div className="flex items-center pt-5">
                    <label className="inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!newBanner.is_active}
                        onChange={(e) => setNewBanner({ ...newBanner, is_active: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="relative w-9 h-5 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-600"></div>
                      <span className="ms-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider">Active Status</span>
                    </label>
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">
                      Banner Image <span className="text-red-400">*</span>
                    </label>

                    <input
                      ref={bannerFileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleBannerFileSelect(file, 'new');
                        e.target.value = '';
                      }}
                      className="hidden"
                    />

                    {bannerLocalPreview ? (
                      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 p-3 bg-slate-950/50 rounded-xl border border-slate-800">
                        <div className="relative w-full sm:w-56 h-28 rounded-lg overflow-hidden bg-slate-950 border border-slate-700 flex items-center justify-center flex-shrink-0 shadow-md">
                          <img src={bannerLocalPreview} alt="Banner Preview" className="w-full h-full object-cover" />
                          <span className="absolute bottom-1 right-1 bg-green-500/90 text-white text-[9px] font-bold px-1.5 py-0.5 rounded shadow">
                            Preview
                          </span>
                        </div>
                        <div className="space-y-2 flex-1">
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => bannerFileInputRef.current?.click()}
                              className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded-lg text-xs font-bold transition flex items-center gap-1.5 shadow"
                            >
                              <LucideIcons.Crop size={14} />
                              Change / Recrop
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setBannerFile(null);
                                setBannerLocalPreview(null);
                              }}
                              className="bg-red-600/10 text-red-400 hover:bg-red-600 hover:text-white px-2.5 py-1.5 rounded-lg text-xs font-bold transition"
                            >
                              Remove
                            </button>
                          </div>
                          <p className="text-[11px] text-emerald-400 font-medium">
                            ✓ Image preview ready! Backend me yeh tabhi upload hoga jab aap "Upload Banner" button par click karenge.
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div
                        onClick={() => bannerFileInputRef.current?.click()}
                        className="border-2 border-dashed border-slate-700 hover:border-indigo-500 rounded-xl p-6 text-center cursor-pointer bg-slate-950/40 hover:bg-slate-800/40 transition-all flex flex-col items-center justify-center gap-2 group"
                      >
                        <div className="w-10 h-10 rounded-full bg-indigo-600/10 flex items-center justify-center text-indigo-400 group-hover:scale-110 transition-transform">
                          <LucideIcons.UploadCloud size={20} />
                        </div>
                        <span className="text-xs font-semibold text-slate-300">Click to select banner image</span>
                        <span className="text-[10px] text-slate-500">Opens 16:9 banner cropper tool • File uploads only upon saving</span>
                      </div>
                    )}
                  </div>

                  <div className="sm:col-span-2 pt-4 flex gap-3 border-t border-gray-800 mt-2">
                    <button type="submit" disabled={addBannerMutation.isPending} className="flex-1 bg-indigo-600 disabled:opacity-50 text-white py-2.5 rounded-lg text-xs font-bold hover:bg-indigo-700 transition shadow">
                      {addBannerMutation.isPending ? 'Uploading...' : 'Upload Banner'}
                    </button>
                    <button type="button" onClick={() => { setIsAddingBanner(false); setBannerFile(null); setBannerLocalPreview(null); }} className="flex-1 bg-slate-800 text-gray-300 py-2.5 rounded-lg text-xs font-bold border border-slate-700 hover:border-slate-600 hover:bg-slate-750 transition">
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          <div className="mb-6">
            <StakeTable
              title="Sliders & Banners"
              columns={bannerColumns}
              data={activeBanners}
              emptyMessage="No banners uploaded yet."
              hasSidebarMargin={false}
              isLoading={paginatedBannersQuery.isLoading}
              page={bannersPage}
              limit={bannersLimit}
              total={bannersPagination.total}
              totalPages={bannersPagination.totalPages}
              onPageChange={(p) => setBannersPage(p)}
              onLimitChange={(l) => {
                setBannersLimit(l);
                setBannersPage(1);
              }}
              onRefresh={() => paginatedBannersQuery.refetch()}
              actions={
                <button
                  type="button"
                  onClick={() => {
                    setIsAddingBanner(true);
                    setBannerFile(null);
                    setBannerLocalPreview(null);
                  }}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold h-9 px-4 rounded-lg transition-colors shadow-lg flex items-center gap-1.5"
                >
                  <LucideIcons.Plus size={14} />
                  Add New Banner
                </button>
              }
            />
          </div>

          {(() => {
            const currentCropType = cropTarget === 'new' ? newBanner.type : (editingBanner?.type || 'promotions');
            const currentCropTheme = cropTarget === 'new' ? activeDesign : (editingBanner?.theme || activeDesign || 'stake');
            const cropInfo = getBannerCropInfo(currentCropType, currentCropTheme);
            return (
              <ImageCropModal
                isOpen={cropModalOpen}
                imageSrc={cropImageSrc}
                aspect={cropInfo.aspect}
                title={cropInfo.title}
                recommendedText={cropInfo.recommendedText}
                onClose={() => {
                  setCropModalOpen(false);
                  setCropImageSrc(null);
                }}
                onConfirm={(croppedFile, previewUrl) => {
                  if (cropTarget === 'new') {
                    setBannerFile(croppedFile);
                    setBannerLocalPreview(previewUrl);
                  } else {
                    setEditBannerFile(croppedFile);
                    setEditBannerLocalPreview(previewUrl);
                  }
                }}
              />
            );
          })()}
        </>
      )}


      {activeCmsTab === 'logos' && (
        <>
          <h1 className="text-xl sm:text-2xl font-bold mb-6 text-white tracking-tight">Logos & Branding</h1>

          {brandConfig ? (
            <div className="space-y-6 w-full">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                {/* CARD 1: GENERAL CONFIGS */}
                <div className="bg-slate-900/60 p-4 sm:p-6 rounded-xl border border-gray-800 shadow-xl space-y-6 flex flex-col justify-between">
                  <div>
                    <h3 className="text-white text-xs font-black uppercase tracking-wider border-b border-gray-800 pb-3 mb-5 flex items-center gap-2">
                      <LucideIcons.Settings size={14} className="text-indigo-400" />
                      General Configurations
                    </h3>

                    <div className="space-y-5">
                      <div>
                        <label className="block text-gray-400 mb-2 font-bold text-xs uppercase tracking-wider">Brand Name</label>
                        <input
                          type="text"
                          value={brandConfig.website_name}
                          onChange={(e) => setBrandConfig({ ...brandConfig, website_name: e.target.value })}
                          className="w-full bg-slate-800/60 text-white rounded-lg p-3 border border-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-xs font-semibold"
                          placeholder="e.g. Winsmash"
                        />
                      </div>

                      <div>
                        <label className="block text-gray-400 mb-2 font-bold text-xs uppercase tracking-wider">Website Theme / Design</label>
                        <select
                          value={brandConfig.active_design || 'bc'}
                          onChange={(e) => setBrandConfig({ ...brandConfig, active_design: e.target.value })}
                          className="w-full bg-slate-800/60 text-white rounded-lg p-3 border border-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-xs font-semibold cursor-pointer"
                        >
                          <option value="bc">BC.Game Design</option>
                          <option value="stake">Stake Design</option>
                        </select>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-gray-400 mb-2 font-bold text-xs uppercase tracking-wider">Admin Panel Timezone</label>
                          <select
                            value={brandConfig.site_timezone || 'Asia/Kolkata'}
                            onChange={(e) => setBrandConfig({ ...brandConfig, site_timezone: e.target.value })}
                            className="w-full bg-slate-800/60 text-white rounded-lg p-3 border border-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-xs font-semibold cursor-pointer"
                          >
                            {getTimezoneListWithOffsets().map((tz) => (
                              <option key={tz.value} value={tz.value}>{tz.label}</option>
                            ))}
                          </select>
                          <span className="text-[10px] text-gray-500 mt-1 block">Used for admin panel reports & analytics</span>
                        </div>

                        <div>
                          <label className="block text-gray-400 mb-2 font-bold text-xs uppercase tracking-wider">Platform & User Timezone</label>
                          <select
                            value={brandConfig.default_user_timezone || 'Asia/Kolkata'}
                            onChange={(e) => setBrandConfig({ ...brandConfig, default_user_timezone: e.target.value })}
                            className="w-full bg-slate-800/60 text-white rounded-lg p-3 border border-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-xs font-semibold cursor-pointer"
                          >
                            {getTimezoneListWithOffsets().map((tz) => (
                              <option key={tz.value} value={tz.value}>{tz.label}</option>
                            ))}
                          </select>
                          <span className="text-[10px] text-gray-500 mt-1 block">Baseline timezone for players & cron jobs</span>
                        </div>
                      </div>

                      <div className="bg-slate-950/40 p-4 rounded-xl border border-slate-800/80 space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <label htmlFor="testing-mode" className="text-gray-200 font-bold text-xs select-none uppercase tracking-wider block">App Testing Mode</label>
                            <span className="text-[10px] text-gray-500">Enable disclaimer marquee & lock password changes</span>
                          </div>
                          <input
                            type="checkbox"
                            id="testing-mode"
                            checked={!!brandConfig.is_testing_mode}
                            onChange={(e) => setBrandConfig({ ...brandConfig, is_testing_mode: e.target.checked })}
                            className="w-4 h-4 text-indigo-600 bg-gray-800 border-gray-700 rounded focus:ring-indigo-500 focus:ring-offset-gray-900 accent-indigo-500 cursor-pointer"
                          />
                        </div>

                        <div className="flex items-center justify-between border-t border-slate-800/60 pt-4">
                          <div>
                            <label htmlFor="m-mode" className="text-gray-200 font-bold text-xs select-none uppercase tracking-wider block">Maintenance Mode</label>
                            <span className="text-[10px] text-gray-500">Temporarily disable public access</span>
                          </div>
                          <input
                            type="checkbox"
                            id="m-mode"
                            checked={brandConfig.maintenance_mode}
                            onChange={(e) => setBrandConfig({ ...brandConfig, maintenance_mode: e.target.checked })}
                            className="w-4 h-4 text-indigo-600 bg-gray-800 border-gray-700 rounded focus:ring-indigo-500 focus:ring-offset-gray-900 accent-indigo-500 cursor-pointer"
                          />
                        </div>

                        <div className="flex items-center justify-between border-t border-slate-800/60 pt-4">
                          <div>
                            <label htmlFor="hide-no-image-games" className="text-gray-200 font-bold text-xs select-none uppercase tracking-wider block">Hide Games Without Images</label>
                            <span className="text-[10px] text-gray-500">Hide games that do not have any image on the website</span>
                          </div>
                          <input
                            type="checkbox"
                            id="hide-no-image-games"
                            checked={!!brandConfig.hide_games_without_images}
                            onChange={(e) => setBrandConfig({ ...brandConfig, hide_games_without_images: e.target.checked })}
                            className="w-4 h-4 text-indigo-600 bg-gray-800 border-gray-700 rounded focus:ring-indigo-500 focus:ring-offset-gray-900 accent-indigo-500 cursor-pointer"
                          />
                        </div>

                        <div className="flex items-center justify-between border-t border-slate-800/60 pt-4">
                          <div>
                            <label htmlFor="app-header-show" className="text-gray-200 font-bold text-xs select-none uppercase tracking-wider block">Show App Download</label>
                            <span className="text-[10px] text-gray-500">Show app link in header & footer</span>
                          </div>
                          <input
                            type="checkbox"
                            id="app-header-show"
                            checked={!!brandConfig.show_app_download}
                            onChange={(e) => setBrandConfig({ ...brandConfig, show_app_download: e.target.checked })}
                            className="w-4 h-4 text-indigo-600 bg-gray-800 border-gray-700 rounded focus:ring-indigo-500 focus:ring-offset-gray-900 accent-indigo-500 cursor-pointer"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* CARD 2: BRAND LOGOS */}
                <div className="bg-slate-900/60 p-4 sm:p-6 rounded-xl border border-gray-800 shadow-xl space-y-6 lg:col-span-2">
                  <h3 className="text-white text-xs font-black uppercase tracking-wider border-b border-gray-800 pb-3 flex items-center gap-2">
                    <LucideIcons.Image size={14} className="text-indigo-400" />
                    Logo Assets & Favicons
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Small Logo */}
                    <div className="bg-slate-800/30 p-5 rounded-xl border border-gray-800 flex flex-col justify-between hover:border-gray-750 transition-all">
                      <div>
                        <label className="block text-gray-400 mb-3 font-bold text-xs uppercase tracking-wider">Small Logo</label>
                        <div className="bg-slate-950/80 rounded-xl p-4 border border-gray-800 h-28 flex items-center justify-center mb-4 overflow-hidden">
                          {brandConfig.logo_small ? (
                            <img src={`${appConfig.baseURL}${brandConfig.logo_small}?t=${Date.now()}`} alt="Small Logo" className="max-h-full max-w-full object-contain" />
                          ) : (
                            <span className="text-gray-600 text-xs italic">No logo uploaded</span>
                          )}
                        </div>
                      </div>
                      <input type="file" accept="image/*" onChange={(e) => setLogoSmallFile(e.target.files?.[0] || null)} className="text-white text-[11px] file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-[11px] file:font-semibold file:bg-indigo-600/10 file:text-indigo-400 hover:file:bg-indigo-600/20" />
                    </div>

                    {/* Big Logo */}
                    <div className="bg-slate-800/30 p-5 rounded-xl border border-gray-800 flex flex-col justify-between hover:border-gray-750 transition-all">
                      <div>
                        <label className="block text-gray-400 mb-3 font-bold text-xs uppercase tracking-wider">Big Logo</label>
                        <div className="bg-slate-950/80 rounded-xl p-4 border border-gray-800 h-28 flex items-center justify-center mb-4 overflow-hidden">
                          {brandConfig.logo_big ? (
                            <img src={`${appConfig.baseURL}${brandConfig.logo_big}?t=${Date.now()}`} alt="Big Logo" className="max-h-full max-w-full object-contain" />
                          ) : (
                            <span className="text-gray-600 text-xs italic">No logo uploaded</span>
                          )}
                        </div>
                      </div>
                      <input type="file" accept="image/*" onChange={(e) => setLogoBigFile(e.target.files?.[0] || null)} className="text-white text-[11px] file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-[11px] file:font-semibold file:bg-indigo-600/10 file:text-indigo-400 hover:file:bg-indigo-600/20" />
                    </div>

                    {/* Favicon */}
                    <div className="bg-slate-800/30 p-5 rounded-xl border border-gray-800 flex flex-col justify-between md:col-span-2 hover:border-gray-750 transition-all">
                      <div>
                        <label className="block text-gray-400 mb-3 font-bold text-xs uppercase tracking-wider">Favicon Icon</label>
                        <div className="bg-slate-950/80 rounded-xl p-4 border border-gray-800 h-20 flex items-center justify-center mb-4 overflow-hidden">
                          {brandConfig.favicon ? (
                            <img src={`${appConfig.baseURL}${brandConfig.favicon}?t=${Date.now()}`} alt="Favicon" className="h-8 w-8 object-contain" />
                          ) : (
                            <span className="text-gray-600 text-xs italic">No favicon uploaded</span>
                          )}
                        </div>
                      </div>
                      <input type="file" accept="image/*,.ico" onChange={(e) => setFaviconFile(e.target.files?.[0] || null)} className="text-white text-[11px] file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-[11px] file:font-semibold file:bg-indigo-600/10 file:text-indigo-400 hover:file:bg-indigo-600/20" />
                    </div>
                  </div>
                </div>

                {/* CARD 3: MOBILE APP PACKAGE */}
                <div className="bg-slate-900/60 p-4 sm:p-6 rounded-xl border border-gray-800 shadow-xl space-y-5 lg:col-span-3">
                  <h3 className="text-white text-xs font-black uppercase tracking-wider border-b border-gray-800 pb-3 flex items-center gap-2">
                    <LucideIcons.Smartphone size={14} className="text-indigo-400" />
                    Android Mobile Application Package
                  </h3>

                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

                    {/* LEFT: Download Name + Upload */}
                    <div className="lg:col-span-2 space-y-4">

                      {/* Download Name */}
                      <div>
                        <label className="block text-gray-400 mb-1.5 font-bold text-xs uppercase tracking-wider flex items-center gap-1.5">
                          <LucideIcons.Tag size={12} className="text-indigo-400" />
                          Download File Name
                          <span className="text-gray-600 font-normal normal-case ml-1">(e.g. WinSmash.apk)</span>
                        </label>
                        <input
                          type="text"
                          value={brandConfig.app_download_name || ''}
                          onChange={(e) => setBrandConfig({ ...brandConfig, app_download_name: e.target.value || null })}
                          className="w-full bg-slate-800/60 text-white rounded-lg p-2.5 border border-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-xs font-mono"
                          placeholder="WinSmash.apk"
                        />
                        <p className="text-gray-600 text-[10px] mt-1">Yeh naam user ke device par download hone ke baad dikhai dega.</p>
                      </div>

                      {/* Upload APK */}
                      <div>
                        <label className="block text-gray-400 mb-1.5 font-bold text-xs uppercase tracking-wider flex items-center gap-1.5">
                          <LucideIcons.Upload size={12} className="text-indigo-400" />
                          Upload New APK File
                        </label>
                        <input
                          type="file"
                          accept=".apk"
                          onChange={(e) => setAppFile(e.target.files?.[0] || null)}
                          className="text-white text-xs w-full file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-bold file:bg-indigo-600 file:text-white hover:file:bg-indigo-700 cursor-pointer"
                        />
                        {brandConfig.app_download_url && (
                          <div className="mt-2 flex items-center gap-2 text-indigo-400 break-all font-mono text-[10px] bg-slate-950/50 p-2 rounded-lg border border-gray-800">
                            <LucideIcons.Link size={11} className="shrink-0" />
                            <span className="text-gray-500">Current file:</span>
                            <a href={`${appConfig.baseURL}${brandConfig.app_download_url}`} target="_blank" rel="noopener noreferrer" className="hover:underline truncate">
                              {brandConfig.app_download_url}
                            </a>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* RIGHT: Direct URL override + Show toggle */}
                    <div className="space-y-4">
                      <div className="bg-slate-950/40 p-4 rounded-xl border border-slate-800/80 space-y-3">
                        <div className="text-[10px] font-black text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                          <LucideIcons.Link2 size={11} className="text-yellow-500" />
                          Direct URL Override
                        </div>
                        <p className="text-gray-600 text-[10px]">Upload ki jagah custom link use karo (Play Store, Google Drive, etc.)</p>
                        <input
                          type="text"
                          value={brandConfig.app_download_url || ''}
                          onChange={(e) => setBrandConfig({ ...brandConfig, app_download_url: e.target.value || null })}
                          className="w-full bg-slate-800/60 text-white rounded-lg p-2.5 border border-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-xs font-mono"
                          placeholder="https://play.google.com/..."
                        />
                      </div>

                      {/* Show/Hide Toggle */}
                      <div className="bg-slate-950/40 p-4 rounded-xl border border-slate-800/80 flex items-center justify-between gap-3">
                        <div>
                          <div className="text-[10px] font-black text-gray-400 uppercase tracking-wider">Show in Header</div>
                          <div className="text-gray-600 text-[10px] mt-0.5">Download button website header mai dikhao</div>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer shrink-0">
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={!!brandConfig.show_app_download}
                            onChange={(e) => setBrandConfig({ ...brandConfig, show_app_download: e.target.checked })}
                          />
                          <div className="w-10 h-5 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-5 after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-600"></div>
                        </label>
                      </div>
                    </div>

                  </div>
                </div>

              </div>

              {/* SAVE BUTTONS */}
              <div className="pt-2">
                <button
                  onClick={handleUpdateConfig}
                  disabled={updateConfigMutation.isPending}
                  className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-bold py-3.5 px-4 rounded-xl text-xs transition-all shadow-lg uppercase tracking-wider flex items-center justify-center gap-2"
                >
                  {updateConfigMutation.isPending ? (
                    <>
                      <LucideIcons.Loader2 size={14} className="animate-spin" /> Saving Configuration...
                    </>
                  ) : (
                    <>
                      <LucideIcons.Save size={14} /> Save Branding & App Configuration
                    </>
                  )}
                </button>
              </div>
            </div>
          ) : (
            <div className="ml-0 lg:ml-[256px] text-white p-4">Loading logo configuration...</div>
          )}
        </>
      )}

      {activeCmsTab === 'footer' && (
        <>
          <h1 className="text-xl sm:text-2xl font-bold mb-6 text-white tracking-tight">Footer Settings</h1>

          <form onSubmit={handleUpdateFooter} className="space-y-6 max-w-4xl">
            {/* General Description and Copyright */}
            <div className="bg-slate-900/60 p-4 sm:p-6 rounded-xl border border-gray-800 shadow-xl space-y-4">
              <h3 className="text-white text-xs font-black uppercase tracking-wider border-b border-gray-800 pb-3">General Footer Info</h3>

              <div>
                <label className="block text-gray-400 mb-1.5 font-bold text-xs uppercase tracking-wider">Copyright Text</label>
                <input
                  type="text"
                  required
                  value={footerCopyright}
                  onChange={(e) => setFooterCopyright(e.target.value)}
                  className="w-full bg-slate-800/60 text-white rounded-lg p-2.5 border border-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-500 text-xs"
                  placeholder="e.g. © 2026 Winsmash | All Rights Reserved."
                />
              </div>

              <div>
                <label className="block text-gray-400 mb-1.5 font-bold text-xs uppercase tracking-wider">Footer Regulatory / Description Text</label>
                <textarea
                  rows={4}
                  required
                  value={footerDescription}
                  onChange={(e) => setFooterDescription(e.target.value)}
                  className="w-full bg-slate-800/60 text-white rounded-lg p-2.5 border border-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-500 text-xs leading-relaxed"
                  placeholder="Company operations description, registrations, comoros gaming license details, etc."
                />
              </div>
            </div>

            {/* Custom Link Columns/Sections */}
            <div className="bg-slate-900/60 p-4 sm:p-6 rounded-xl border border-gray-800 shadow-xl space-y-5">
              <div className="flex justify-between items-center border-b border-gray-800 pb-3">
                <h3 className="text-white text-xs font-black uppercase tracking-wider">Footer Columns (Support, About, FAQ, etc.)</h3>
                <button
                  type="button"
                  onClick={handleAddSection}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white text-[11px] font-bold py-2 px-4 rounded-lg transition-all shadow flex items-center gap-1.5"
                >
                  <LucideIcons.Plus size={13} /> Add Column
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {footerSections.map((sec, secIdx) => (
                  <div key={secIdx} className="bg-slate-900/45 p-5 rounded-xl border border-slate-800 space-y-4 flex flex-col justify-between hover:border-slate-750 transition-all duration-200 shadow-lg">
                    <div>
                      <div className="flex justify-between items-center gap-3 mb-2">
                        <input
                          type="text"
                          required
                          value={sec.title}
                          onChange={(e) => handleSectionTitleChange(secIdx, e.target.value)}
                          className="bg-transparent border-b border-gray-700 text-white font-black text-sm focus:outline-none focus:border-indigo-500 pb-1 w-full"
                          placeholder="Column Title"
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveSection(secIdx)}
                          className="text-red-400 hover:text-red-300 text-xs font-bold shrink-0 bg-red-950/20 hover:bg-red-950/40 px-3 py-1.5 rounded-lg border border-red-500/10 transition-colors flex items-center gap-1"
                        >
                          <LucideIcons.Trash2 size={12} /> Remove
                        </button>
                      </div>

                      {/* Links list in section */}
                      <div className="space-y-4 mt-4">
                        {sec.links && sec.links.map((link: any, linkIdx: number) => (
                          <div key={linkIdx} className="bg-slate-950/40 hover:bg-slate-950/70 p-4 rounded-xl border border-slate-800/80 space-y-3 relative group transition-all duration-200">
                            <div className="flex justify-between items-center gap-2">
                              <span className="text-[10px] font-bold text-indigo-400 uppercase tracking-wide">Link #{linkIdx + 1}</span>
                              <button
                                type="button"
                                onClick={() => handleRemoveLink(secIdx, linkIdx)}
                                className="text-red-400 hover:text-red-300 text-[10px] font-bold flex items-center gap-1 bg-red-950/20 hover:bg-red-950/40 px-2 py-1 rounded transition-colors"
                              >
                                <LucideIcons.Trash2 size={10} /> Delete
                              </button>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Label</label>
                                <input
                                  type="text"
                                  required
                                  value={link.label}
                                  onChange={(e) => handleLinkChange(secIdx, linkIdx, 'label', e.target.value)}
                                  className="w-full bg-slate-900/60 border border-slate-700/60 text-white text-xs rounded-lg p-2 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                                  placeholder="e.g. FAQ"
                                />
                              </div>
                              <div>
                                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">URL / Route</label>
                                <input
                                  type="text"
                                  required
                                  value={link.to}
                                  onChange={(e) => handleLinkChange(secIdx, linkIdx, 'to', e.target.value)}
                                  className="w-full bg-slate-900/60 border border-slate-700/60 text-white text-xs rounded-lg p-2 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                                  placeholder="e.g. /faq"
                                />
                              </div>
                            </div>
                            <div className="flex items-center gap-2 pt-1">
                              <input
                                type="checkbox"
                                id={`link-ext-${secIdx}-${linkIdx}`}
                                checked={!!link.external}
                                onChange={(e) => handleLinkChange(secIdx, linkIdx, 'external', e.target.checked)}
                                className="rounded border-slate-700 bg-slate-900 accent-indigo-500 w-3.5 h-3.5"
                              />
                              <label htmlFor={`link-ext-${secIdx}-${linkIdx}`} className="text-[11px] text-gray-400 select-none font-medium cursor-pointer">
                                Open in new tab / Download file
                              </label>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => handleAddLink(secIdx)}
                      className="w-full mt-4 bg-slate-800/80 hover:bg-slate-850 text-gray-300 text-xs font-bold py-2 px-3 rounded-lg border border-slate-700 hover:border-slate-600 transition-all flex items-center justify-center gap-1.5"
                    >
                      <LucideIcons.Plus size={14} /> Add Link
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Save Buttons */}
            <button
              type="submit"
              disabled={updateConfigMutation.isPending}
              className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-bold py-3.5 px-4 rounded-xl text-xs transition shadow-lg uppercase tracking-wider"
            >
              {updateConfigMutation.isPending ? 'Saving Settings...' : 'Save Footer Settings'}
            </button>
          </form>
        </>
      )}

      {activeCmsTab === 'social' && (
        <>
          <div className="flex items-center justify-between gap-4 mb-6">
            <h1 className="text-xl sm:text-2xl font-bold text-white tracking-tight">
              Social Links & Support
            </h1>
            {!editingSocialLink && (
              <button
                type="button"
                onClick={() => {
                  setEditingSocialLink({ name: '', url: '', icon_name: 'Globe', show_in_footer: true, show_in_sidebar: false, is_enabled: true, type: 'default' });
                  setIsAddingSocialLink(true);
                  setSocialFile(null);
                }}
                className="bg-green-600 hover:bg-green-700 text-white text-xs font-bold py-2 px-3 rounded-md transition-colors shadow-sm whitespace-nowrap"
              >
                + Add Social Link
              </button>
            )}
          </div>

          {editingSocialLink ? (
            /* Add/Edit Social Link Form */
            <div className="bg-slate-900/60 p-4 sm:p-6 rounded-xl border border-gray-800 shadow-xl w-full mb-6">
              <h3 className="text-white text-sm font-black uppercase tracking-wider border-b border-gray-800 pb-3 mb-4">
                {isAddingSocialLink ? 'Add Social Media link' : 'Edit Social Media link'}
              </h3>
              <form onSubmit={handleSaveSocialLink} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Left Column: Form Fields & Checkboxes */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-gray-400 mb-1.5 font-bold text-xs uppercase tracking-wider">Platform Name</label>
                      <input
                        type="text"
                        required
                        value={editingSocialLink.name}
                        onChange={(e) => setEditingSocialLink({ ...editingSocialLink, name: e.target.value })}
                        className="w-full bg-slate-800/60 text-white rounded-lg p-2.5 border border-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-500 text-xs"
                        placeholder="e.g. Telegram Channel"
                      />
                    </div>

                    <div>
                      <label className="block text-gray-400 mb-1.5 font-bold text-xs uppercase tracking-wider">Link type</label>
                      <select
                        value={editingSocialLink.type || 'default'}
                        onChange={(e) => setEditingSocialLink({ ...editingSocialLink, type: e.target.value })}
                        className="w-full bg-slate-800/60 text-white rounded-lg p-2.5 border border-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-500 text-xs"
                      >
                        <option value="default">Default Link (Redirection URL)</option>
                        <option value="live_chat">Live Support ( Crisp / Live Chat script trigger )</option>
                      </select>
                    </div>

                    {editingSocialLink.type !== 'live_chat' && (
                      <div>
                        <label className="block text-gray-400 mb-1.5 font-bold text-xs uppercase tracking-wider">Redirect URL</label>
                        <input
                          type="text"
                          required
                          value={editingSocialLink.url}
                          onChange={(e) => setEditingSocialLink({ ...editingSocialLink, url: e.target.value })}
                          className="w-full bg-slate-800/60 text-white rounded-lg p-2.5 border border-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-500 text-xs"
                          placeholder="e.g. https://t.me/your_channel"
                        />
                      </div>
                    )}

                    <div>
                      <label className="block text-gray-400 mb-1.5 font-bold text-xs uppercase tracking-wider">Upload Custom Image Icon (Optional)</label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => setSocialFile(e.target.files?.[0] || null)}
                        className="text-white text-xs mt-2"
                      />
                    </div>

                    <div className="bg-slate-800/40 p-4 rounded-lg border border-gray-750 flex flex-col gap-3">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="social-foot"
                          checked={!!editingSocialLink.show_in_footer}
                          onChange={(e) => setEditingSocialLink({ ...editingSocialLink, show_in_footer: e.target.checked })}
                          className="rounded border-gray-700 bg-gray-900 accent-indigo-500 w-3.5 h-3.5"
                        />
                        <label htmlFor="social-foot" className="text-xs font-bold text-gray-300 select-none">
                          Show in Footer Row
                        </label>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="social-side"
                          checked={!!editingSocialLink.show_in_sidebar}
                          onChange={(e) => setEditingSocialLink({ ...editingSocialLink, show_in_sidebar: e.target.checked })}
                          className="rounded border-gray-700 bg-gray-900 accent-indigo-500 w-3.5 h-3.5"
                        />
                        <label htmlFor="social-side" className="text-xs font-bold text-gray-300 select-none">
                          Show in Sidebar Column
                        </label>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="social-act"
                          checked={!!editingSocialLink.is_enabled}
                          onChange={(e) => setEditingSocialLink({ ...editingSocialLink, is_enabled: e.target.checked })}
                          className="rounded border-gray-700 bg-gray-900 accent-indigo-500 w-3.5 h-3.5"
                        />
                        <label htmlFor="social-act" className="text-xs font-bold text-gray-300 select-none">
                          Enabled / Active
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Right Column: Icon Selection Grid */}
                  <div className="space-y-4">
                    <label className="block text-gray-400 mb-1.5 font-bold text-xs uppercase tracking-wider">Social Icon Selection</label>
                    <div className="grid grid-cols-6 gap-2 p-3 bg-slate-950/50 border border-slate-700/50 rounded-lg h-[280px] overflow-y-auto">
                      {SOCIAL_ICONS_LIST.map((item) => {
                        const IconComponent = (LucideIcons as any)[item] || LucideIcons.Globe;
                        const isSelected = editingSocialLink.icon_name === item;
                        return (
                          <button
                            key={item}
                            type="button"
                            onClick={() => setEditingSocialLink({ ...editingSocialLink, icon_name: item })}
                            title={item}
                            className={`aspect-square p-2 rounded-lg flex items-center justify-center transition-all ${isSelected
                              ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/30 scale-110'
                              : 'bg-slate-900 border border-slate-800 text-slate-400 hover:border-slate-600 hover:text-white'
                              }`}
                          >
                            <IconComponent size={20} />
                          </button>
                        );
                      })}
                    </div>
                    {editingSocialLink.icon_name && (
                      <div className="flex items-center gap-2 mt-1 text-[10px] text-indigo-400 font-bold uppercase tracking-wider">
                        Selected Icon: {editingSocialLink.icon_name}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex gap-2 pt-4 border-t border-gray-800">
                  <button
                    type="submit"
                    className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold py-2.5 px-4 rounded-lg flex-1 shadow"
                  >
                    Save Changes
                  </button>
                  <button
                    type="button"
                    onClick={() => { setEditingSocialLink(null); setIsAddingSocialLink(false); setSocialFile(null); }}
                    className="bg-gray-700 hover:bg-gray-650 text-white text-xs font-bold py-2.5 px-4 rounded-lg shadow"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          ) : (
            /* Social Links List Table */
            <div className="overflow-x-auto border border-gray-800 rounded-xl bg-slate-900/60 shadow-xl">
              <table className="w-full text-left text-xs text-gray-300">
                <thead className="text-[10px] uppercase bg-slate-800 text-gray-400 border-b border-gray-800 tracking-wider font-bold">
                  <tr>
                    <th className="px-4 py-3">Platform</th>
                    <th className="px-4 py-3">Link/Redirection</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Footer</th>
                    <th className="px-4 py-3">Sidebar</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {socialLinks.length > 0 ? (
                    socialLinks.map((link) => {
                      const IconComponent = (LucideIcons as any)[link.icon_name || 'Globe'] || LucideIcons.Globe;
                      return (
                        <tr key={link.id} className="border-b border-gray-850 hover:bg-slate-850/50">
                          <td className="px-4 py-3 font-semibold text-white flex items-center gap-2.5">
                            {link.icon_url ? (
                              <img src={`${appConfig.baseURL}${link.icon_url}`} alt={link.name} className="w-6 h-6 rounded-md object-contain p-0.5 bg-slate-950 border border-gray-800" />
                            ) : (
                              <div className="w-6 h-6 rounded-md bg-slate-850 border border-gray-700 flex items-center justify-center text-indigo-400">
                                <IconComponent size={14} />
                              </div>
                            )}
                            <div>
                              <div>{link.name}</div>
                              {link.type === 'live_chat' && <span className="text-[9px] font-bold text-emerald-400 uppercase tracking-wider">Crisp Support</span>}
                            </div>
                          </td>
                          <td className="px-4 py-3 font-mono text-gray-400">
                            {link.type === 'live_chat' ? '— ( Crisp Trigger )' : link.url || '—'}
                          </td>
                          <td className="px-4 py-3">
                            <button
                              type="button"
                              onClick={() => handleToggleSocialLink(link.id, 'is_enabled')}
                              className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-250 ease-in-out focus:outline-none ${link.is_enabled ? 'bg-emerald-600' : 'bg-rose-500'
                                }`}
                            >
                              <span
                                className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-250 ease-in-out ${link.is_enabled ? 'translate-x-4' : 'translate-x-0'
                                  }`}
                              />
                            </button>
                          </td>
                          <td className="px-4 py-3">
                            <button
                              type="button"
                              onClick={() => handleToggleSocialLink(link.id, 'show_in_footer')}
                              className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-250 ease-in-out focus:outline-none ${link.show_in_footer ? 'bg-indigo-600' : 'bg-rose-500'
                                }`}
                            >
                              <span
                                className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-250 ease-in-out ${link.show_in_footer ? 'translate-x-4' : 'translate-x-0'
                                  }`}
                              />
                            </button>
                          </td>
                          <td className="px-4 py-3">
                            <button
                              type="button"
                              onClick={() => handleToggleSocialLink(link.id, 'show_in_sidebar')}
                              className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-250 ease-in-out focus:outline-none ${link.show_in_sidebar ? 'bg-indigo-600' : 'bg-rose-500'
                                }`}
                            >
                              <span
                                className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-250 ease-in-out ${link.show_in_sidebar ? 'translate-x-4' : 'translate-x-0'
                                  }`}
                              />
                            </button>
                          </td>
                          <td className="px-4 py-3 text-right space-x-2">
                            <button
                              type="button"
                              onClick={() => { setEditingSocialLink(link); setIsAddingSocialLink(false); setSocialFile(null); }}
                              className="text-indigo-400 hover:text-indigo-300 font-bold"
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteSocialLink(link.id)}
                              className="text-red-400 hover:text-red-300 font-bold ml-2"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-gray-500 font-semibold">
                        No social links configured. Add your first link!
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {activeCmsTab === 'help' && (
        <>
          <h1 className="text-xl sm:text-2xl font-bold mb-6 text-white tracking-tight">
            {helpIsCreatingNew ? 'Create Help Center Section' : helpIsEditing ? 'Edit Help Center Section' : 'Help Center Management'}
          </h1>

          {!helpIsCreatingNew && !helpIsEditing ? (
            <div className="mb-6">
              <StakeTable
                title="Help Sections List"
                columns={helpColumns}
                data={helpPages.filter((p: any) =>
                  p.title.toLowerCase().includes(helpSearchQuery.toLowerCase()) ||
                  p.slug.toLowerCase().includes(helpSearchQuery.toLowerCase())
                )}
                searchPlaceholder="Search help sections..."
                onSearch={(val) => setHelpSearchQuery(val)}
                emptyMessage="No help sections found."
                hasSidebarMargin={false}
                isLoading={isHelpLoading}
                actions={
                  <button
                    type="button"
                    onClick={handleCreateNewHelpTabClick}
                    className="bg-[#646ECD] hover:bg-[#525ab5] text-white text-sm font-medium py-2 px-3 rounded-md flex items-center gap-2 transition-colors shadow-sm whitespace-nowrap"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Create New Section
                  </button>
                }
              />
            </div>
          ) : (
            /* Help Center Form Editor Area */
            <div className="bg-slate-900/60 p-4 sm:p-6 rounded-xl border border-gray-800 shadow-xl h-fit">
              <form onSubmit={handleSaveHelpPage} className="space-y-4">
                <div className="flex justify-between items-center border-b border-gray-800 pb-3">
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => { setHelpIsCreatingNew(false); setHelpIsEditing(false); }}
                      className="text-gray-400 hover:text-white text-xs font-bold flex items-center gap-1 bg-slate-800/50 hover:bg-slate-800 px-3 py-1.5 rounded-lg border border-gray-750 transition-all"
                    >
                      <i className="fas fa-arrow-left"></i> Back to list
                    </button>
                    <h3 className="text-xs font-black uppercase text-white tracking-wide">
                      {helpIsCreatingNew ? 'Create New Help Section' : `Edit Section: ${helpSlug}`}
                    </h3>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="help-active"
                      checked={helpIsActive}
                      onChange={(e) => setHelpIsActive(e.target.checked)}
                      className="rounded border-gray-700 bg-gray-800 accent-indigo-500 w-3.5 h-3.5"
                    />
                    <label htmlFor="help-active" className="text-xs font-bold text-gray-300 select-none">
                      Active
                    </label>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">
                      Section Title
                    </label>
                    <input
                      type="text"
                      required
                      value={helpTitle}
                      onChange={(e) => setHelpTitle(e.target.value)}
                      className="w-full bg-slate-800/60 border border-gray-700 text-white rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500"
                      placeholder="e.g. FAQ"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">
                      Section Slug (URL query tab name)
                    </label>
                    <input
                      type="text"
                      required
                      value={helpSlug}
                      onChange={(e) => setHelpSlug(e.target.value)}
                      className="w-full bg-slate-800/60 border border-gray-700 text-white rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500"
                      placeholder="e.g. faq"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">
                      Sort Order
                    </label>
                    <input
                      type="number"
                      required
                      value={helpSortOrder}
                      onChange={(e) => setHelpSortOrder(parseInt(e.target.value, 10) || 0)}
                      className="w-full bg-slate-800/60 border border-gray-700 text-white rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500"
                      placeholder="0"
                    />
                  </div>
                </div>

                <div className="bg-slate-950/40 p-4 rounded-xl border border-gray-800 space-y-3">
                  <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                    Section Icon Selection
                  </label>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <span className="block text-[9px] font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                        Select Standard Icon
                      </span>
                      <div className="grid grid-cols-7 gap-2 p-2.5 bg-slate-950/50 border border-slate-800 rounded-lg h-[110px] overflow-y-auto">
                        {Array.from({ length: 13 }, (_, i) => {
                          const path = `/assets/bcimages/provablyfair/svgexport-${i + 1}.svg`;
                          const isSelected = helpIconUrl === path;
                          const fullUrl = `${appConfig.frontendURL}${path}`;
                          return (
                            <button
                              key={path}
                              type="button"
                              onClick={() => setHelpIconUrl(path)}
                              title={`Icon ${i + 1}`}
                              className={`aspect-square p-1.5 rounded-lg flex items-center justify-center transition-all bg-slate-900 border ${isSelected
                                ? 'border-indigo-500 bg-indigo-600/20 shadow-md scale-105'
                                : 'border-slate-800 text-slate-400 hover:border-slate-650 hover:text-white'
                                }`}
                            >
                              <img src={fullUrl} alt={`Icon ${i + 1}`} className="w-6 h-6 object-contain" />
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="flex flex-col justify-between">
                      <div>
                        <label className="block text-[9px] font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                          Or enter custom Icon Path / URL
                        </label>
                        <input
                          type="text"
                          value={helpIconUrl}
                          onChange={(e) => setHelpIconUrl(e.target.value)}
                          className="w-full bg-slate-800/60 border border-gray-700 text-white rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500"
                          placeholder="e.g. /assets/images/taraju.png"
                        />
                      </div>

                      {helpIconUrl && (
                        <div className="flex items-center gap-3 mt-3 p-2 bg-slate-950/40 rounded-lg border border-slate-800/60">
                          <div className="w-10 h-10 bg-slate-900 border border-slate-800 rounded flex items-center justify-center shrink-0">
                            <img
                              src={helpIconUrl.startsWith('http') ? helpIconUrl : (helpIconUrl.startsWith('/') ? `${appConfig.frontendURL}${helpIconUrl}` : helpIconUrl)}
                              alt="Selected Icon"
                              className="w-7 h-7 object-contain"
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.opacity = '0';
                              }}
                              onLoad={(e) => {
                                (e.target as HTMLImageElement).style.opacity = '1';
                              }}
                            />
                          </div>
                          <div>
                            <span className="block text-[8px] font-bold text-indigo-400 uppercase tracking-wider">Currently Selected</span>
                            <span className="text-[10px] text-gray-400 break-all font-mono">{helpIconUrl}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">
                    Section HTML Content
                  </label>
                  <div className="bg-slate-800 rounded-lg border border-gray-700 text-white overflow-hidden jodit-dark-container">
                    <JoditEditor
                      value={helpContent}
                      config={joditConfig as any}
                      onBlur={newContent => setHelpContent(newContent)}
                      onChange={newContent => { }}
                    />
                  </div>
                </div>

                <div className="flex justify-between gap-3 pt-2">
                  <button
                    type="submit"
                    className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold py-2.5 px-4 rounded-lg transition-colors shadow-lg"
                  >
                    Save Help Section
                  </button>
                  {helpSelectedId && (
                    <button
                      type="button"
                      onClick={() => handleDeleteHelpPage(helpSelectedId)}
                      className="px-5 py-2.5 bg-red-600/20 text-red-400 hover:bg-red-650 hover:text-white rounded-lg text-xs font-bold transition-all"
                    >
                      Delete Section
                    </button>
                  )}
                </div>
              </form>
            </div>
          )}
        </>
      )}

      {activeCmsTab === 'game_cards' && (
        <>
          <h1 className="text-xl sm:text-2xl font-bold mb-6 text-white tracking-tight">Home Game Cards Management</h1>

          <form onSubmit={handleUpdateGameCards} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {gameCards.map((card, index) => (
                <div key={card.id || index} className="bg-slate-900/60 p-5 rounded-xl border border-gray-800 shadow-xl flex flex-col gap-4">
                  <div className="flex justify-between items-center border-b border-gray-800 pb-2">
                    <span className="text-xs font-black uppercase text-indigo-400 tracking-wide">
                      Card #{index + 1}: {card.name || 'Unnamed'}
                    </span>
                    <span className="text-[10px] text-gray-500 font-mono">{card.id}</span>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">
                        Card Title
                      </label>
                      <input
                        type="text"
                        required
                        value={card.name || ''}
                        onChange={(e) => handleCardChange(index, 'name', e.target.value)}
                        className="w-full bg-slate-800/60 border border-gray-700 text-white rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        placeholder="e.g. Slots"
                      />
                    </div>

                    <div>
                      <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">
                        Action Type
                      </label>
                      <select
                        value={card.action_type || 'redirect'}
                        onChange={(e) => handleCardChange(index, 'action_type', e.target.value)}
                        className="w-full bg-slate-800/60 border border-gray-700 text-white rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500"
                      >
                        <option value="redirect">Redirect Link</option>
                        <option value="launch_game">Launch Game (Monopoly Live)</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">
                      Redirection Path / Route
                    </label>
                    <input
                      type="text"
                      disabled={card.action_type === 'launch_game'}
                      value={card.path || ''}
                      onChange={(e) => handleCardChange(index, 'path', e.target.value)}
                      className="w-full bg-slate-800/60 border border-gray-700 text-white rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:opacity-40"
                      placeholder="e.g. /casino/slots"
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">
                      Description Text
                    </label>
                    <textarea
                      rows={2}
                      value={card.desc || ''}
                      onChange={(e) => handleCardChange(index, 'desc', e.target.value)}
                      className="w-full bg-slate-800/60 border border-gray-700 text-white rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500"
                      placeholder="Enter description text shown below the title"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">
                        Background Image Path
                      </label>
                      <input
                        type="text"
                        value={card.bg_image || ''}
                        onChange={(e) => handleCardChange(index, 'bg_image', e.target.value)}
                        className="w-full bg-slate-800/60 border border-gray-700 text-white rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500 mb-1.5"
                        placeholder="/assets/images/bg.png"
                      />
                      <input
                        type="file"
                        accept="image/*"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            try {
                              const formData = new FormData();
                              formData.append('file', file);
                              const res = await apiClient.post('/admin/upload-gamecard-asset', formData, {
                                headers: {
                                  'Content-Type': 'multipart/form-data',
                                  authorization: localStorage.getItem('token') || ''
                                }
                              });
                              if (res.data?.url) {
                                handleCardChange(index, 'bg_image', res.data.url);
                                toast.success('Background image uploaded successfully!');
                              }
                            } catch (err) {
                              toast.error('Failed to upload background image');
                            }
                          }
                        }}
                        className="text-white text-[10px] mt-1 block"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">
                        Icon SVG Path
                      </label>
                      <input
                        type="text"
                        value={card.icon || ''}
                        onChange={(e) => handleCardChange(index, 'icon', e.target.value)}
                        className="w-full bg-slate-800/60 border border-gray-700 text-white rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500 mb-1.5"
                        placeholder="/assets/bcimages/icon.svg"
                      />
                      <input
                        type="file"
                        accept="image/*"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            try {
                              const formData = new FormData();
                              formData.append('file', file);
                              const res = await apiClient.post('/admin/upload-gamecard-asset', formData, {
                                headers: {
                                  'Content-Type': 'multipart/form-data',
                                  authorization: localStorage.getItem('token') || ''
                                }
                              });
                              if (res.data?.url) {
                                handleCardChange(index, 'icon', res.data.url);
                                toast.success('Icon uploaded successfully!');
                              }
                            } catch (err) {
                              toast.error('Failed to upload icon');
                            }
                          }
                        }}
                        className="text-white text-[10px] mt-1 block"
                      />
                    </div>
                  </div>

                  <div className="flex gap-4 items-center mt-2 bg-slate-950/40 p-2.5 rounded-lg border border-slate-800/60">
                    {card.bg_image && (
                      <div className="flex flex-col items-center">
                        <span className="text-[8px] font-bold text-gray-500 mb-1 uppercase">Background</span>
                        <div className="w-16 h-10 bg-slate-900 border border-slate-800 rounded overflow-hidden flex items-center justify-center">
                          <img src={card.bg_image.startsWith('http') ? card.bg_image : (card.bg_image.startsWith('/assets/') ? `${appConfig.frontendURL}${card.bg_image}` : `${appConfig.baseURL}${card.bg_image}`)} alt="BG" className="w-full h-full object-cover" />
                        </div>
                      </div>
                    )}
                    {card.icon && (
                      <div className="flex flex-col items-center">
                        <span className="text-[8px] font-bold text-gray-500 mb-1 uppercase">Icon SVG</span>
                        <div className="w-10 h-10 bg-slate-900 border border-slate-800 rounded flex items-center justify-center">
                          <img src={card.icon.startsWith('http') ? card.icon : (card.icon.startsWith('/assets/') ? `${appConfig.frontendURL}${card.icon}` : `${appConfig.baseURL}${card.icon}`)} alt="Icon" className="w-6 h-6 object-contain" />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-slate-900/60 p-4 rounded-xl border border-gray-800 flex justify-end">
              <button
                type="submit"
                disabled={updateConfigMutation.isPending}
                className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-xs font-bold py-2.5 px-6 rounded-lg transition-colors shadow-lg"
              >
                {updateConfigMutation.isPending ? 'Saving changes...' : 'Save Game Cards Config'}
              </button>
            </div>
          </form>
        </>
      )}

      {activeCmsTab === 'affiliate' && (
        <>
          <h1 className="text-xl sm:text-2xl font-bold mb-6 text-white tracking-tight">Affiliate CMS Management</h1>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Overview Config */}
            <div className="lg:col-span-2 bg-slate-900/60 p-4 sm:p-6 rounded-xl border border-gray-800 shadow-xl space-y-6">
              <h2 className="text-base font-black uppercase text-indigo-400 tracking-wide border-b border-gray-800 pb-2">
                Overview Config (Metrics, Advantages & Commission Rules)
              </h2>

              <form onSubmit={handleUpdateAffiliateOverview} className="space-y-6">
                {/* Metrics */}
                <div className="space-y-4">
                  <h3 className="text-xs font-bold text-gray-300 uppercase tracking-wider">Metrics</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {affiliateOverview?.metrics?.map((metric: any, index: number) => (
                      <div key={index} className="bg-slate-950/40 p-4 rounded-lg border border-slate-800">
                        <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">
                          Metric #{index + 1} Number/Value
                        </label>
                        <input
                          type="text"
                          value={metric.num || ''}
                          onChange={(e) => {
                            const updatedMetrics = [...affiliateOverview.metrics];
                            updatedMetrics[index].num = e.target.value;
                            setAffiliateOverview({ ...affiliateOverview, metrics: updatedMetrics });
                          }}
                          className="w-full bg-slate-800/60 border border-gray-700 text-white rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500 mb-2"
                        />
                        <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">
                          Metric #{index + 1} Label
                        </label>
                        <input
                          type="text"
                          value={metric.label || ''}
                          onChange={(e) => {
                            const updatedMetrics = [...affiliateOverview.metrics];
                            updatedMetrics[index].label = e.target.value;
                            setAffiliateOverview({ ...affiliateOverview, metrics: updatedMetrics });
                          }}
                          className="w-full bg-slate-800/60 border border-gray-700 text-white rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Advantages */}
                <div className="space-y-4">
                  <h3 className="text-xs font-bold text-gray-300 uppercase tracking-wider">Advantages Cards</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {affiliateOverview?.advantages?.map((adv: any, index: number) => (
                      <div key={index} className="bg-slate-950/40 p-4 rounded-lg border border-slate-800 space-y-2">
                        <span className="text-[10px] font-black uppercase text-indigo-400">Card #{index + 1}</span>
                        <div>
                          <label className="block text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-1">Title</label>
                          <input
                            type="text"
                            value={adv.title || ''}
                            onChange={(e) => {
                              const updatedAdvs = [...affiliateOverview.advantages];
                              updatedAdvs[index].title = e.target.value;
                              setAffiliateOverview({ ...affiliateOverview, advantages: updatedAdvs });
                            }}
                            className="w-full bg-slate-800/60 border border-gray-700 text-white rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500"
                          />
                        </div>
                        <div>
                          <label className="block text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-1">Description</label>
                          <textarea
                            rows={2}
                            value={adv.desc || ''}
                            onChange={(e) => {
                              const updatedAdvs = [...affiliateOverview.advantages];
                              updatedAdvs[index].desc = e.target.value;
                              setAffiliateOverview({ ...affiliateOverview, advantages: updatedAdvs });
                            }}
                            className="w-full bg-slate-800/60 border border-gray-700 text-white rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Commission Rules */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xs font-bold text-gray-300 uppercase tracking-wider">Commission Rules</h3>
                    <button
                      type="button"
                      onClick={() => {
                        const newRule = { icon: "HelpCircle", title: "New Rule", desc: "", formula: "" };
                        setAffiliateOverview({
                          ...affiliateOverview,
                          commissionRules: [...(affiliateOverview.commissionRules || []), newRule]
                        });
                      }}
                      className="flex items-center gap-1 bg-green-600/10 text-green-400 hover:bg-green-600 hover:text-white px-2.5 py-1 rounded-lg text-[10px] font-bold transition-colors border border-green-500/20"
                    >
                      <span className="text-sm leading-none">+</span> Add Rule
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {(affiliateOverview?.commissionRules || []).map((rule: any, index: number) => (
                      <div key={index} className="bg-slate-950/40 p-4 rounded-lg border border-slate-800 space-y-2 relative">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[10px] font-black uppercase text-amber-400">Rule #{index + 1}</span>
                          <button
                            type="button"
                            onClick={() => {
                              const updated = (affiliateOverview.commissionRules || []).filter((_: any, idx: number) => idx !== index);
                              setAffiliateOverview({ ...affiliateOverview, commissionRules: updated });
                            }}
                            className="text-red-400 hover:text-red-300 text-[10px] font-bold hover:bg-red-500/10 px-1.5 py-0.5 rounded transition-colors"
                          >
                            ✕ Remove
                          </button>
                        </div>
                        <div>
                          <label className="block text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-1">Icon Name (Lucide)</label>
                          <input
                            type="text"
                            value={rule.icon || ''}
                            onChange={(e) => {
                              const updated = [...(affiliateOverview.commissionRules || [])];
                              updated[index] = { ...updated[index], icon: e.target.value };
                              setAffiliateOverview({ ...affiliateOverview, commissionRules: updated });
                            }}
                            placeholder="e.g. Spade, Award, Club"
                            className="w-full bg-slate-800/60 border border-gray-700 text-white rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500 font-mono"
                          />
                        </div>
                        <div>
                          <label className="block text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-1">Title</label>
                          <input
                            type="text"
                            value={rule.title || ''}
                            onChange={(e) => {
                              const updated = [...(affiliateOverview.commissionRules || [])];
                              updated[index] = { ...updated[index], title: e.target.value };
                              setAffiliateOverview({ ...affiliateOverview, commissionRules: updated });
                            }}
                            className="w-full bg-slate-800/60 border border-gray-700 text-white rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500"
                          />
                        </div>
                        <div>
                          <label className="block text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-1">Description</label>
                          <textarea
                            rows={2}
                            value={rule.desc || ''}
                            onChange={(e) => {
                              const updated = [...(affiliateOverview.commissionRules || [])];
                              updated[index] = { ...updated[index], desc: e.target.value };
                              setAffiliateOverview({ ...affiliateOverview, commissionRules: updated });
                            }}
                            className="w-full bg-slate-800/60 border border-gray-700 text-white rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500"
                          />
                        </div>
                        <div>
                          <label className="block text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-1">Formula</label>
                          <input
                            type="text"
                            value={rule.formula || ''}
                            onChange={(e) => {
                              const updated = [...(affiliateOverview.commissionRules || [])];
                              updated[index] = { ...updated[index], formula: e.target.value };
                              setAffiliateOverview({ ...affiliateOverview, commissionRules: updated });
                            }}
                            placeholder="e.g. (0.03 * wagered / 2) * commission rate"
                            className="w-full bg-slate-800/60 border border-gray-700 text-white rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500 font-mono"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end pt-2">
                  <button
                    type="submit"
                    disabled={updateConfigMutation.isPending}
                    className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-xs font-bold py-2.5 px-6 rounded-lg transition-colors shadow-lg"
                  >
                    {updateConfigMutation.isPending ? 'Saving Overview...' : 'Save Overview Settings'}
                  </button>
                </div>
              </form>
            </div>

            {/* Terms & Conditions Editor */}
            <div className="bg-slate-900/60 p-4 sm:p-6 rounded-xl border border-gray-800 shadow-xl space-y-6">
              <h2 className="text-base font-black uppercase text-indigo-400 tracking-wide border-b border-gray-800 pb-2">
                Terms & Conditions Modal Text
              </h2>

              <form onSubmit={handleUpdateAffiliateTerms} className="space-y-4">
                <div>
                  <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">
                    Terms Content (HTML / Plain Text)
                  </label>
                  <textarea
                    rows={15}
                    value={affiliateTerms}
                    onChange={(e) => setAffiliateTerms(e.target.value)}
                    className="w-full bg-slate-800/60 border border-gray-700 text-white rounded-lg px-3 py-2 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    placeholder="Enter terms and conditions content..."
                  />
                </div>

                <div className="flex justify-end pt-2">
                  <button
                    type="submit"
                    disabled={updateConfigMutation.isPending}
                    className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-xs font-bold py-2.5 px-6 rounded-lg transition-colors shadow-lg"
                  >
                    {updateConfigMutation.isPending ? 'Saving Terms...' : 'Save Terms & Conditions'}
                  </button>
                </div>
              </form>
            </div>

            {/* FAQ Manager */}
            <div className="lg:col-span-3 bg-slate-900/60 p-4 sm:p-6 rounded-xl border border-gray-800 shadow-xl space-y-6">
              <div className="flex justify-between items-center border-b border-gray-800 pb-2">
                <h2 className="text-base font-black uppercase text-indigo-400 tracking-wide">
                  FAQ Manager
                </h2>
                {!isAddingFaq && editingFaqIndex === null && (
                  <button
                    type="button"
                    onClick={() => {
                      setIsAddingFaq(true);
                      setEditingFaqIndex(null);
                      setFaqQuestion('');
                      setFaqAnswer('');
                      setFaqCategory('General');
                    }}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold py-1.5 px-3 rounded-lg transition-colors"
                  >
                    Add New FAQ
                  </button>
                )}
              </div>

              {(isAddingFaq || editingFaqIndex !== null) ? (
                <form onSubmit={handleSaveFaq} className="bg-slate-950/40 p-4 rounded-lg border border-indigo-500/30 space-y-4">
                  <h3 className="text-xs font-bold text-indigo-400 uppercase">
                    {isAddingFaq ? 'Add New FAQ Item' : `Edit FAQ Item #${editingFaqIndex! + 1}`}
                  </h3>
                  <div>
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Category (Tab)</label>
                    <select
                      value={faqCategory}
                      onChange={(e) => setFaqCategory(e.target.value)}
                      className="w-full bg-slate-800/60 border border-gray-700 text-white rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    >
                      <option value="General">General</option>
                      <option value="Affiliate Program">Affiliate Program</option>
                      <option value="Earnings">Earnings</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Question</label>
                    <input
                      type="text"
                      required
                      value={faqQuestion}
                      onChange={(e) => setFaqQuestion(e.target.value)}
                      className="w-full bg-slate-800/60 border border-gray-700 text-white rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Answer</label>
                    <textarea
                      rows={3}
                      required
                      value={faqAnswer}
                      onChange={(e) => setFaqAnswer(e.target.value)}
                      className="w-full bg-slate-800/60 border border-gray-700 text-white rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button type="submit" disabled={updateConfigMutation.isPending} className="bg-indigo-600 text-white text-xs font-bold py-2 px-4 rounded-lg hover:bg-indigo-700 transition">
                      {updateConfigMutation.isPending ? 'Saving...' : 'Save FAQ'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setIsAddingFaq(false);
                        setEditingFaqIndex(null);
                        setFaqQuestion('');
                        setFaqAnswer('');
                        setFaqCategory('General');
                      }}
                      className="bg-gray-800 text-gray-300 text-xs font-bold py-2 px-4 rounded-lg hover:bg-gray-700 transition"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <div className="w-full">
                  <StakeTable
                    title="Affiliate FAQs"
                    columns={faqColumns}
                    data={affiliateFaqs}
                    emptyMessage="No FAQs configured yet."
                    hasSidebarMargin={false}
                  />
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
